import {
  AlertTriangle,
  BarChart3,
  CalendarDays,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  ClipboardList,
  Download,
  FileText,
  Globe2,
  Home,
  LogOut,
  MessageCircle,
  Plus,
  Rocket,
  Save,
  ShieldCheck,
  Store,
  Trash2,
  Upload,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import {
  AdminUser,
  BoardMember,
  applyDirectoryMemberImport,
  CertificationChecklistItemInput,
  CertificationChecklistStatus,
  CertificationSigner,
  CertificationSignerDirectory,
  CertificationSignerProfileInput,
  CertificationReviewInput,
  createAdminUser,
  createLaunchEvent,
  createDirectoryAdditionalLead,
  createDirectoryMember,
  createDirectoryTeam,
  CsvImportPreview,
  deleteDirectoryAdditionalLead,
  deleteAdminUser,
  deleteCertificationSignerSignature,
  deleteDirectoryMember,
  FormKind,
  FlightReviewInput,
  getTeamAccessConfig,
  getTeamDirectoryAccess,
  getTeamDirectorySummary,
  getCertificationSigners,
  getLaunchDaySummary,
  getLaunchFlightCard,
  getLaunchSubmission,
  listDirectoryMembers,
  listDirectoryTeams,
  listAccessUsers,
  listCertificationSignerProfiles,
  listLaunchFlightCards,
  listLaunchEvents,
  listLaunchOperationEvents,
  listUpcomingLaunchEvents,
  logoutTeamAccess,
  previewDirectoryMemberImport,
  requestTeamAccess,
  resendSignedCertificationForm,
  removeTemporaryCertificationAuthorization,
  saveTemporaryCertificationAuthorization,
  saveSubmission,
  SaveResult,
  syncManifestDirectoryMembers,
  TeamAccount,
  TeamAdditionalLead,
  TeamAdditionalLeadInput,
  TeamAccountInput,
  TeamDirectoryAccess,
  TeamDirectorySummary,
  TeamMember,
  TeamMemberInput,
  LaunchDaySummary,
  LaunchEvent,
  LaunchEventInput,
  LaunchEventSummary,
  LaunchFlightCardDetail,
  LaunchFlightCardSummary,
  LaunchSubmissionDetail,
  LaunchSubmissionSummary,
  listLaunchSubmissions,
  updateDirectoryMember,
  updateDirectoryAdditionalLead,
  updateDirectoryTeam,
  updateCertificationReview,
  updateCertificationSignerProfile,
  updateFlightReview,
  updateLaunchEvent,
  uploadCertificationSignerSignature,
  useCertificationSignerFacsimile,
  signedCertificationFormUrl,
  verifyDirectoryTeam,
  verifyTeamAccess,
} from './api';

type Route = 'home' | 'manifest' | 'flight-card' | 'certification-flight' | 'project-flight' | 'team-directory' | 'team-summary' | 'launch-summary' | 'launch-operations' | 'admins';

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: { sitekey: string; callback: (token: string) => void; 'error-callback': () => void }) => string;
      remove: (widgetId: string) => void;
    };
  }
}

type MotorRow = {
  id: string;
  stage: string;
  count: string;
  manufacturer: string;
  designation: string;
  impulse: string;
  notes: string;
};

const MAX_MOTOR_ROWS = 6;

type ManifestPerson = {
  id: string;
  day: string;
  name: string;
  email: string;
  narTra: string;
  expDate: string;
  notes: string;
};

type ManifestFlyer = ManifestPerson & {
  group: string;
  flightType: string;
  rocketName: string;
  rocketColors: string;
};

type ManifestRosterRole = 'none' | 'flyer' | 'nonFlyer';

const basePath = '/urrg';

const routePageMetadata: Record<Route, { title: string; icon: string }> = {
  home: { title: 'URRG Forms', icon: 'home' },
  manifest: { title: 'Team Manifest | URRG Forms', icon: 'manifest' },
  'flight-card': { title: 'Flight Card | URRG Forms', icon: 'flight-card' },
  'certification-flight': { title: 'Certification Flight | URRG Forms', icon: 'certification-flight' },
  'project-flight': { title: 'Project Flight | URRG Forms', icon: 'project-flight' },
  'team-directory': { title: 'Team Directory | URRG Forms', icon: 'team-directory' },
  'team-summary': { title: 'School Summary | URRG Forms', icon: 'team-summary' },
  'launch-summary': { title: 'Launch Day Summary | URRG Forms', icon: 'launch-summary' },
  'launch-operations': { title: 'Launch Operations | URRG Forms', icon: 'launch-summary' },
  admins: { title: 'Administrator Management | URRG Forms', icon: 'admins' },
};

function hasAdministrativeAccess(access: TeamDirectoryAccess | null | undefined): boolean {
  return access?.role === 'admin' || access?.role === 'board';
}

function newId(prefix: string) {
  return `${prefix}-${Math.random().toString(36).slice(2, 9)}`;
}

function blankMotor(): MotorRow {
  return {
    id: newId('motor'),
    stage: '1',
    count: '1',
    manufacturer: '',
    designation: '',
    impulse: '',
    notes: '',
  };
}

function blankFlyer(): ManifestFlyer {
  return {
    id: newId('flyer'),
    day: '',
    group: '',
    name: '',
    email: '',
    narTra: '',
    expDate: '',
    notes: '',
    flightType: 'Standard',
    rocketName: '',
    rocketColors: '',
  };
}

function blankPerson(): ManifestPerson {
  return {
    id: newId('person'),
    day: '',
    name: '',
    email: '',
    narTra: '',
    expDate: '',
    notes: '',
  };
}

function manifestMembership(member: TeamMember): { narTra: string; expDate: string; notes: string } {
  const memberships = [
    member.narNumber && `NAR ${member.narNumber}`,
    member.traNumber && `TRA ${member.traNumber}`,
  ].filter(Boolean).join(' | ');
  return {
    narTra: memberships,
    expDate: member.narExpiration || member.traExpiration || member.membershipExpiration,
    notes: member.certificationLevel && member.certificationLevel !== 'None'
      ? `Certification: ${member.certificationLevel}`
      : '',
  };
}

function manifestPersonFromMember(member: TeamMember): ManifestPerson {
  return {
    id: newId('roster-person'),
    day: '',
    name: member.fullName,
    email: member.email,
    ...manifestMembership(member),
  };
}

function manifestFlyerFromMember(member: TeamMember): ManifestFlyer {
  return {
    ...manifestPersonFromMember(member),
    id: newId('roster-flyer'),
    group: '',
    flightType: 'Standard',
    rocketName: '',
    rocketColors: '',
  };
}

function appendManifestRosterRows<T extends ManifestPerson>(existing: T[], additions: T[]): T[] {
  const filled = existing.filter((row) => row.name || row.email);
  const existingKeys = new Set(filled.map((row) => `${row.email.trim().toLowerCase()}|${row.name.trim().toLowerCase()}`));
  return [
    ...filled,
    ...additions.filter((row) => !existingKeys.has(`${row.email.trim().toLowerCase()}|${row.name.trim().toLowerCase()}`)),
  ];
}

function manifestDirectoryPeople(rows: ManifestPerson[]) {
  return rows
    .filter((row) => row.name.trim())
    .map(({ name, email, narTra, expDate, notes }) => ({
      fullName: name.trim(),
      email: email.trim(),
      narTra: narTra.trim(),
      expDate: expDate || null,
      notes: notes.trim(),
    }));
}

function getRouteFromPath(): Route {
  const trimmed = window.location.pathname.replace(/^\/urrg\/?/, '').replace(/\/$/, '');
  if (
    trimmed === 'manifest' ||
    trimmed === 'flight-card' ||
    trimmed === 'certification-flight' ||
    trimmed === 'project-flight' ||
    trimmed === 'team-directory' ||
    trimmed === 'team-summary' ||
    trimmed === 'launch-summary' ||
    trimmed === 'launch-operations' ||
    trimmed === 'admins'
  ) {
    return trimmed;
  }
  return 'home';
}

function navigate(route: Route) {
  const nextPath = route === 'home' ? `${basePath}/` : `${basePath}/${route}`;
  window.history.pushState({}, '', nextPath);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function navigateToTeamDirectory(returnTo = '') {
  const query = returnTo ? `?returnTo=${encodeURIComponent(returnTo)}` : '';
  window.history.pushState({}, '', `${basePath}/team-directory${query}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function navigateToDirectoryTeam(teamId: string) {
  window.history.pushState({}, '', `${basePath}/team-directory?teamId=${encodeURIComponent(teamId)}`);
  window.dispatchEvent(new PopStateEvent('popstate'));
}

function teamAccessReturnPath() {
  const returnTo = new URLSearchParams(window.location.search).get('returnTo');
  return returnTo === `${basePath}/` || returnTo === `${basePath}/manifest` || returnTo === `${basePath}/launch-operations` || returnTo === `${basePath}/admins` ? returnTo : '';
}

function requestedDirectoryTeamId() {
  return new URLSearchParams(window.location.search).get('teamId') || '';
}

function Field(props: {
  label: string;
  children: ReactNode;
  help?: string;
  required?: boolean;
}) {
  return (
    <label className="field">
      <span>
        {props.label}
        {props.required ? <b> *</b> : null}
      </span>
      {props.children}
      {props.help ? <small>{props.help}</small> : null}
    </label>
  );
}

function Status({ result }: { result: SaveResult | null }) {
  if (!result) return null;
  if (result.mode === 'server') {
    return (
      <p className="status good">
        <CheckCircle2 size={18} /> Submission received. Reference: {result.referenceId}.
      </p>
    );
  }
  return (
    <p className="status bad" role="alert">
      <AlertTriangle size={18} /> {result.message}
    </p>
  );
}

function PageShell({
  title,
  eyebrow,
  children,
}: {
  title: string;
  eyebrow: string;
  children: ReactNode;
}) {
  return (
    <>
      <header className="topbar">
        <button className="iconButton" type="button" onClick={() => navigate('home')} title="Home">
          <Home size={20} />
        </button>
        <div>
          <span>{eyebrow}</span>
          <h1>{title}</h1>
        </div>
      </header>
      {children}
    </>
  );
}

function UrrgHeader() {
  return (
    <header className="siteHeader">
      <div className="siteHeaderInner">
        <a className="siteBrand" href="https://www.urrg.us/">
          <Rocket size={24} aria-hidden="true" />
          <span>
            <strong>URRG</strong>
            <small>Upstate Research Rocketry Group</small>
          </span>
        </a>
      </div>
    </header>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

function ActionBar({
  result,
  isSubmitting,
  children,
}: {
  result: SaveResult | null;
  isSubmitting: boolean;
  children?: ReactNode;
}) {
  return (
    <div className="actionBar">
      {children}
      <button className="primaryButton" type="submit" disabled={isSubmitting}>
        <Save size={18} /> {isSubmitting ? 'Submitting...' : 'Submit form'}
      </button>
      <Status result={result} />
    </div>
  );
}

function Honeypot() {
  return (
    <div className="honeypot" aria-hidden="true">
      <label htmlFor="website">Website</label>
      <input id="website" name="website" type="text" tabIndex={-1} autoComplete="off" />
    </div>
  );
}

function getHoneypot(form: HTMLFormElement) {
  const value = new FormData(form).get('website');
  return typeof value === 'string' ? value : '';
}

function useSubmission() {
  const [result, setResult] = useState<SaveResult | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const idempotencyKey = useRef<string | null>(null);

  async function submit(kind: FormKind, payload: unknown, honeypot: string, launchEventId: string): Promise<SaveResult | undefined> {
    if (isSubmitting) return;
    idempotencyKey.current ??= crypto.randomUUID();
    setIsSubmitting(true);
    const nextResult = await saveSubmission(kind, payload, honeypot, idempotencyKey.current, launchEventId);
    setResult(nextResult);
    if (nextResult.mode === 'server') idempotencyKey.current = null;
    setIsSubmitting(false);
    return nextResult;
  }

  return { result, isSubmitting, submit };
}

type LaunchEventSelection = {
  events: LaunchEvent[];
  selectedEventId: string;
  selectEvent: (eventId: string) => void;
  loading: boolean;
  error: string;
};

function useLaunchEventSelection(): LaunchEventSelection {
  const [events, setEvents] = useState<LaunchEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const result = await listUpcomingLaunchEvents();
        if (!active) return;
        setEvents(result.events);
        setSelectedEventId(result.currentEventId || result.events[0]?.id || '');
      } catch (nextError) {
        if (active) setError(nextError instanceof Error ? nextError.message : 'Unable to load upcoming launch weekends.');
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => { active = false; };
  }, []);

  return { events, selectedEventId, selectEvent: setSelectedEventId, loading, error };
}

function LaunchWeekendSelector({ selection }: { selection: LaunchEventSelection }) {
  return (
    <Section title="Launch Weekend">
      <div className="grid two">
        <Field label="Launch weekend" required>
          <select
            value={selection.selectedEventId}
            onChange={(event) => selection.selectEvent(event.target.value)}
            required
            disabled={selection.loading || !selection.events.length}
          >
            <option value="">{selection.loading ? 'Loading launch weekends...' : 'Select launch weekend'}</option>
            {selection.events.map((event) => (
              <option key={event.id} value={event.id}>
                {formatLaunchWeekend(event)}{event.isCurrent ? ' (next launch)' : ''}
              </option>
            ))}
          </select>
        </Field>
      </div>
      {selection.error ? <p className="status bad" role="alert"><AlertTriangle size={18} /> {selection.error}</p> : null}
      {!selection.loading && !selection.error && !selection.events.length ? <p className="status bad" role="alert"><AlertTriangle size={18} /> No upcoming launch weekends are currently scheduled.</p> : null}
    </Section>
  );
}

function MotorsEditor({
  flightType,
  motors,
  onChange,
  singleMotorOnly = false,
}: {
  flightType: string;
  motors: MotorRow[];
  onChange: (motors: MotorRow[]) => void;
  singleMotorOnly?: boolean;
}) {
  function patchMotor(id: string, patch: Partial<MotorRow>) {
    onChange(motors.map((motor) => (motor.id === id ? { ...motor, ...patch } : motor)));
  }

  function removeMotor(id: string) {
    if (motors.length === 1) return;
    onChange(motors.filter((motor) => motor.id !== id));
  }

  return (
    <div className="repeater">
      <div className="repeaterHeader">
        <h3>Motors</h3>
        {flightType !== 'Standard' && motors.length < MAX_MOTOR_ROWS ? (
          <button type="button" onClick={() => onChange([...motors, { ...blankMotor(), stage: String(motors.length + 1) }])}>
            <Plus size={17} /> Add motor
          </button>
        ) : null}
      </div>
      {motors.map((motor, index) => (
        <div className={`motorRow ${flightType === 'Multistage' ? 'withStage' : ''}`} key={motor.id}>
          <strong>#{index + 1}</strong>
          {flightType === 'Multistage' ? (
            <Field label="Stage">
              <input
                type="number"
                min="1"
                max="20"
                value={motor.stage}
                onChange={(event) => patchMotor(motor.id, { stage: event.target.value })}
                required
              />
            </Field>
          ) : null}
          <Field label="Qty">
            <input
              type="number"
              min="1"
              max={singleMotorOnly ? 1 : 20}
              value={singleMotorOnly ? '1' : motor.count}
              onChange={(event) => patchMotor(motor.id, { count: event.target.value })}
              disabled={singleMotorOnly}
              required
            />
          </Field>
          <Field label="Manufacturer">
            <input value={motor.manufacturer} onChange={(event) => patchMotor(motor.id, { manufacturer: event.target.value })} />
          </Field>
          <Field label="Designation">
            <input value={motor.designation} onChange={(event) => patchMotor(motor.id, { designation: event.target.value })} required />
          </Field>
          <Field label="Impulse">
            <input value={motor.impulse} onChange={(event) => patchMotor(motor.id, { impulse: event.target.value })} />
          </Field>
          <Field label="Notes">
            <input value={motor.notes} onChange={(event) => patchMotor(motor.id, { notes: event.target.value })} />
          </Field>
          {!singleMotorOnly ? (
            <button className="iconButton danger" type="button" title="Remove motor" onClick={() => removeMotor(motor.id)}>
              <Trash2 size={18} />
            </button>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function AltitudeNotice({ altitude }: { altitude: string }) {
  const value = Number(altitude);
  if (!Number.isFinite(value) || value <= 0) return null;
  if (value > 18000) {
    return <p className="callout bad">No flights over 18,000 ft. Revise the plan before submitting.</p>;
  }
  if (value >= 10000) {
    return <p className="callout warn">Flights at or above 10,000 ft need prior approval. Email URRG at crp@urrg.us.</p>;
  }
  return <p className="callout good">Altitude is below the prior-approval threshold.</p>;
}

function StabilityNotice({ cg, cp, diameter }: { cg: string; cp: string; diameter: string }) {
  const cgValue = Number(cg);
  const cpValue = Number(cp);
  const diameterValue = Number(diameter);
  if (![cgValue, cpValue, diameterValue].every((value) => Number.isFinite(value) && value > 0)) return null;

  const calibers = (cpValue - cgValue) / diameterValue;
  const rounded = Math.round(calibers * 100) / 100;
  if (calibers >= 1) {
    return <p className="callout good">Stability margin is {rounded} calibers.</p>;
  }
  return <p className="callout bad">Stability margin is {rounded} calibers. CG should be at least one body diameter forward of CP.</p>;
}

function AgeNotice({ birthDate, certAttempt }: { birthDate: string; certAttempt: string }) {
  if (!birthDate) return null;
  const birth = new Date(`${birthDate}T00:00:00`);
  if (Number.isNaN(birth.getTime())) return null;
  const today = new Date();
  let age = today.getFullYear() - birth.getFullYear();
  const monthDiff = today.getMonth() - birth.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age -= 1;
  if (age >= 18) return <p className="callout good">Age check: {age}. Adult high-power requirement met.</p>;
  if (certAttempt === 'TMP' || certAttempt === 'JrHPP') {
    return <p className="callout warn">Age check: {age}. Minor program selected.</p>;
  }
  return <p className="callout bad">Age check: {age}. High-power certification normally requires age 18 or a youth program path.</p>;
}

function FlightDetailsFields(props: {
  altitude: string;
  setAltitude: (value: string) => void;
  launchGuide: string;
  setLaunchGuide: (value: string) => void;
  flightType: string;
  setFlightType: (value: string) => void;
  motors: MotorRow[];
  setMotors: (motors: MotorRow[]) => void;
  towerNeeded: string;
  setTowerNeeded: (value: string) => void;
  towerSource: string;
  setTowerSource: (value: string) => void;
  recoveryMethod: string;
  setRecoveryMethod: (value: string) => void;
  recoveryOther: string;
  setRecoveryOther: (value: string) => void;
  redundantDeployment: string;
  setRedundantDeployment: (value: string) => void;
  redundancyMethod: string;
  setRedundancyMethod: (value: string) => void;
  flightComputer: string;
  setFlightComputer: (value: string) => void;
  mainDeployAltitude: string;
  setMainDeployAltitude: (value: string) => void;
  tracking: string;
  setTracking: (value: string) => void;
  trackingType: string;
  setTrackingType: (value: string) => void;
  specialInfo: string;
  setSpecialInfo: (value: string) => void;
  notes: string;
  setNotes: (value: string) => void;
  standardOnly?: boolean;
}) {
  function changeFlightType(value: string) {
    if (props.standardOnly) return;
    props.setFlightType(value);
    if (value === 'Standard' && props.motors.length > 1) {
      props.setMotors([props.motors[0]]);
    }
  }

  return (
    <>
      <div className="grid four">
        <Field label="Expected altitude" help="Feet. Prior approval begins at 10,000 ft; maximum is 18,000 ft.">
          <input
            type="number"
            min="0"
            max={18000}
            step="1"
            value={props.altitude}
            onChange={(event) => props.setAltitude(event.target.value)}
            required
          />
        </Field>
        <Field label="Launch guide">
          <select value={props.launchGuide} onChange={(event) => props.setLaunchGuide(event.target.value)}>
            <option value=""></option>
            <option>Rod 1/8</option>
            <option>Rod 3/16</option>
            <option>Rod 1/4</option>
            <option>Rail 1010</option>
            <option>Rail 1515</option>
            <option>Rail Unistrut</option>
          </select>
        </Field>
        <Field label="Need a tower?">
          <select value={props.towerNeeded} onChange={(event) => props.setTowerNeeded(event.target.value)}>
            <option value="No">No</option>
            <option value="Yes">Yes</option>
          </select>
        </Field>
        {props.towerNeeded === 'Yes' ? (
          <Field label="Tower source">
            <select value={props.towerSource} onChange={(event) => props.setTowerSource(event.target.value)}>
              <option value="Own tower">Own tower</option>
              <option value="Club 20 ft hydraulic tower">Club 20 ft hydraulic tower</option>
            </select>
          </Field>
        ) : null}
      </div>
      <AltitudeNotice altitude={props.altitude} />
      <div className="grid three flightConfiguration">
        <Field label="Flight type">
          <select value={props.standardOnly ? 'Standard' : props.flightType} onChange={(event) => changeFlightType(event.target.value)}>
            <option>Standard</option>
            <option disabled={props.standardOnly}>Cluster</option>
            <option disabled={props.standardOnly}>Multistage</option>
          </select>
        </Field>
      </div>
      <MotorsEditor
        flightType={props.standardOnly ? 'Standard' : props.flightType}
        motors={props.standardOnly ? props.motors.slice(0, 1) : props.motors}
        onChange={props.setMotors}
        singleMotorOnly={props.standardOnly}
      />
      <div className="checkboxLine">
        <span>Recovery deployment</span>
        {['Motor ejection', 'Dual deployment', 'Other'].map((option) => (
          <label key={option}>
            <input
              type="radio"
              name="recoveryMethod"
              value={option}
              checked={props.recoveryMethod === option}
              onChange={(event) => props.setRecoveryMethod(event.target.value)}
              required
            />
            {option}
          </label>
        ))}
      </div>
      {props.recoveryMethod === 'Other' ? (
        <div className="grid two">
          <Field label="Other recovery method" required>
            <input value={props.recoveryOther} onChange={(event) => props.setRecoveryOther(event.target.value)} required />
          </Field>
        </div>
      ) : null}
      <div className="checkboxLine">
        <span>Redundant deployment?</span>
        {['No', 'Yes'].map((option) => (
          <label key={option}>
            <input
              type="radio"
              name="redundantDeployment"
              value={option}
              checked={props.redundantDeployment === option}
              onChange={(event) => props.setRedundantDeployment(event.target.value)}
              required
            />
            {option}
          </label>
        ))}
      </div>
      {props.redundantDeployment === 'Yes' ? (
        <div className="checkboxLine">
          <span>Redundancy method</span>
          {['Backup electronics', 'Motor ejection backup'].map((option) => (
            <label key={option}>
              <input
                type="radio"
                name="redundancyMethod"
                value={option}
                checked={props.redundancyMethod === option}
                onChange={(event) => props.setRedundancyMethod(event.target.value)}
                required
              />
              {option}
            </label>
          ))}
        </div>
      ) : null}
      {props.recoveryMethod === 'Dual deployment' ? (
        <div className="grid two">
          <Field label="Flight computer type" required>
            <input value={props.flightComputer} onChange={(event) => props.setFlightComputer(event.target.value)} required />
          </Field>
          <Field label="Main deployment altitude" required help="Feet above ground level.">
            <input
              type="number"
              min="0"
              max={props.altitude || undefined}
              step="1"
              value={props.mainDeployAltitude}
              onChange={(event) => props.setMainDeployAltitude(event.target.value)}
              required
            />
          </Field>
        </div>
      ) : null}
      <div className="checkboxLine">
        <span>Tracking?</span>
        {['No', 'Yes'].map((option) => (
          <label key={option}>
            <input
              type="radio"
              name="tracking"
              value={option}
              checked={props.tracking === option}
              onChange={(event) => props.setTracking(event.target.value)}
              required
            />
            {option}
          </label>
        ))}
      </div>
      {props.tracking === 'Yes' ? (
        <div className="grid two">
          <Field label="Tracking type" required>
            <input value={props.trackingType} onChange={(event) => props.setTrackingType(event.target.value)} required />
          </Field>
        </div>
      ) : null}
      <div className="grid two">
        <Field label="Special info / comments">
          <textarea value={props.specialInfo} onChange={(event) => props.setSpecialInfo(event.target.value)} />
        </Field>
        <Field label="Notes">
          <textarea value={props.notes} onChange={(event) => props.setNotes(event.target.value)} />
        </Field>
      </div>
    </>
  );
}

function HomeRouteCard(props: { route: Route; title: string; description: string; icon: ReactNode }) {
  return (
    <button className="routeCard" type="button" onClick={() => navigate(props.route)}>
      {props.icon}
      <strong>{props.title}</strong>
      <span>{props.description}</span>
    </button>
  );
}

function HomeExternalCard(props: { href: string; title: string; description: string; icon: ReactNode }) {
  return (
    <a className="routeCard" href={props.href} target="_blank" rel="noreferrer">
      {props.icon}
      <strong>{props.title}</strong>
      <span>{props.description}</span>
    </a>
  );
}

function HomeLinkSection(props: { title: string; children: ReactNode }) {
  return (
    <section className="homeLinkSection">
      <h2>{props.title}</h2>
      <div className="routeGrid">{props.children}</div>
    </section>
  );
}

function HomePage() {
  return (
    <main className="home">
      <div className="homeIntro">
        <span>URRG</span>
        <h1>Rocketry Forms</h1>
      </div>
      <HomeLinkSection title="Flight Forms">
        <HomeRouteCard route="flight-card" title="Flight Card" description="Submit a flight card for an upcoming URRG launch." icon={<Rocket />} />
        <HomeRouteCard route="certification-flight" title="Certification Flight" description="Submit a NAR or Tripoli certification flight." icon={<FileText />} />
        <HomeRouteCard route="project-flight" title="Project Flight" description="Submit a team or individual project flight." icon={<Users />} />
        <HomeRouteCard route="manifest" title="Team Manifest" description="Submit your team roster, flyers, and non-flyers." icon={<ClipboardList />} />
      </HomeLinkSection>
      <HomeLinkSection title="Community">
        <HomeExternalCard href="https://discord.gg/stXceWxkxZ" title="URRG Discord" description="Join the URRG Discord server." icon={<MessageCircle />} />
        <HomeExternalCard href="https://www.urrg.us" title="URRG Website" description="Visit the Upstate Research Rocketry Group website." icon={<Globe2 />} />
        <HomeExternalCard href="https://www.facebook.com/groups/urrgny" title="URRG Facebook" description="Join the URRG community on Facebook." icon={<Users />} />
        <HomeExternalCard href="https://discord.gg/uQresRGREP" title="r/Rocketry Discord" description="Join the broader rocketry community on Discord." icon={<MessageCircle />} />
      </HomeLinkSection>
      <HomeLinkSection title="Useful Information">
        <HomeExternalCard href="https://www.mountainmanrockets.com/" title="Mountain Man Rockets" description="Browse high-power rocketry kits, motors, and supplies." icon={<Store />} />
      </HomeLinkSection>
      <HomeLinkSection title="Operations and Admin">
        <HomeRouteCard route="launch-operations" title="Launch Operations" description="LCO workspace for flight results, recovery, and landing status." icon={<ClipboardList />} />
        <HomeRouteCard route="launch-summary" title="Launch Day Summary" description="Review launch submissions, certifications, projects, and attendance." icon={<CalendarDays />} />
        <HomeRouteCard route="team-directory" title="Team Directory" description="Manage team accounts, member rosters, and CSV imports." icon={<Users />} />
        <HomeRouteCard route="team-summary" title="School Summary" description="Review school participation and membership status." icon={<BarChart3 />} />
        <HomeRouteCard route="admins" title="Administrator Management" description="Review URRG administrators, board members, and certification signers." icon={<ShieldCheck />} />
      </HomeLinkSection>
    </main>
  );
}

function emptyTeamAccount(): TeamAccountInput {
  return {
    teamName: '',
    school: '',
    teamEmail: '',
    teamDiscordChannel: '',
    primaryTeamLeadName: '',
    primaryTeamLeadEmail: '',
    primaryTeamLeadPhone: '',
    primaryTeamLeadDiscordUsername: '',
    primaryTeamLeadExpiration: '',
    graduationYear: null,
    clubexpressMemberNumber: '',
    crpMembershipStatus: 'Active',
    membershipStart: '',
    membershipEnd: '',
    notes: '',
    facultyAdvisor: '',
    schoolBillingContact: '',
  };
}

function emptyLaunchEvent(): LaunchEventInput {
  return {
    name: '',
    startDate: '',
    endDate: '',
    status: 'Scheduled',
    acceptingSubmissions: false,
  };
}

function certificationSignerProfileDraft(signer?: CertificationSigner): CertificationSignerProfileInput {
  return {
    email: signer?.email || '',
    narNumber: signer?.narNumber || '',
    narExpiration: signer?.narExpiration || '',
    traNumber: signer?.traNumber || '',
    certificationLevel: signer?.certificationLevel || '',
  };
}

function emptyAdditionalTeamLead(): TeamAdditionalLeadInput {
  return { fullName: '', email: '', phone: '', discordUsername: '', expirationDate: '' };
}

function emptyTeamMember(membershipStatus: TeamMemberInput['membershipStatus'] = 'Active'): TeamMemberInput {
  return {
    fullName: '',
    email: '',
    phone: '',
    discordUsername: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'United States',
    graduationYear: null,
    certificationLevel: 'None',
    narNumber: '',
    narExpiration: '',
    traNumber: '',
    traExpiration: '',
    l2ExamPassed: null,
    l2ExamDate: '',
    notes: '',
    memberNumber: '',
    membershipStatus,
    membershipExpiration: '',
  };
}

function formatDirectoryTimestamp(value: string): string {
  if (!value) return 'Not recorded';
  const timestamp = new Date(`${value}Z`);
  return Number.isNaN(timestamp.getTime())
    ? value
    : new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' }).format(timestamp);
}

function formatLaunchWeekend(event: Pick<LaunchEvent, 'name' | 'startDate' | 'endDate'>): string {
  const formatDate = (value: string) => new Intl.DateTimeFormat('en-US', {
    month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC',
  }).format(new Date(`${value}T12:00:00Z`));
  const dateText = event.startDate === event.endDate
    ? formatDate(event.startDate)
    : `${formatDate(event.startDate)} - ${formatDate(event.endDate)}`;
  return `${event.name} (${dateText})`;
}

function formatReviewTimestamp(value: string): string {
  if (!value) return 'Not recorded';
  const date = new Date(`${value}Z`);
  return Number.isNaN(date.getTime())
    ? value
    : new Intl.DateTimeFormat('en-US', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'America/New_York' }).format(date);
}

function submissionFieldLabel(key: string): string {
  const labels: Record<string, string> = {
    certOrg: 'Certification organization',
    certAttempt: 'Certification attempt',
    currentCertLevel: 'Current certification level',
    cg: 'CG from nose',
    cp: 'CP from nose',
    diameter: 'Rocket diameter',
    lengthIn: 'Rocket length',
    weightLb: 'Rocket weight',
    mainDeployAltitude: 'Main deployment altitude',
    traExpDate: 'Tripoli expiration',
    narExpDate: 'NAR expiration',
    zipCode: 'ZIP / postal code',
    urrgMember: 'URRG member',
  };
  if (labels[key]) return labels[key];
  return key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    .replace(/^./, (value) => value.toUpperCase());
}

function submissionFieldValue(value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Not provided';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) return value.map((item) => submissionFieldValue(item)).join('; ');
  if (typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>)
      .map(([key, item]) => `${submissionFieldLabel(key)}: ${submissionFieldValue(item)}`)
      .join('; ');
  }
  return String(value);
}

function reviewActionLabel(action: string): string {
  return action.replace(/_/g, ' ').replace(/^./, (value) => value.toUpperCase());
}

function numericYear(value: string): number | null {
  const trimmed = value.trim();
  return trimmed ? Number(trimmed) : null;
}

function TurnstileWidget({ siteKey, onToken }: { siteKey: string; onToken: (token: string) => void }) {
  const container = useRef<HTMLDivElement | null>(null);
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!siteKey || !container.current) return undefined;
    let active = true;
    const render = () => {
      if (!active || !container.current || !window.turnstile || widgetId.current) return;
      widgetId.current = window.turnstile.render(container.current, {
        sitekey: siteKey,
        callback: onToken,
        'error-callback': () => onToken(''),
      });
    };
    const existing = document.querySelector<HTMLScriptElement>('script[data-urrg-turnstile]');
    if (existing) {
      if (window.turnstile) render();
      else existing.addEventListener('load', render, { once: true });
    } else {
      const script = document.createElement('script');
      script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';
      script.async = true;
      script.defer = true;
      script.dataset.urrgTurnstile = 'true';
      script.addEventListener('load', render, { once: true });
      document.head.appendChild(script);
    }
    return () => {
      active = false;
      if (widgetId.current && window.turnstile) window.turnstile.remove(widgetId.current);
      widgetId.current = null;
    };
  }, [onToken, siteKey]);

  return <div className="turnstile" ref={container} />;
}

function SummaryMetric({ label, value }: { label: string; value: number }) {
  return <div className="summaryMetric"><dt>{label}</dt><dd>{value}</dd></div>;
}

function TeamSummaryPage() {
  const [access, setAccess] = useState<TeamDirectoryAccess | null>(null);
  const [summary, setSummary] = useState<TeamDirectorySummary | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const nextAccess = await getTeamDirectoryAccess();
        if (!active) return;
        setAccess(nextAccess);
        if (hasAdministrativeAccess(nextAccess)) setSummary(await getTeamDirectorySummary());
      } catch (nextError) {
        if (active) setError(nextError instanceof Error ? nextError.message : 'Unable to load the school summary.');
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => { active = false; };
  }, []);

  if (loading) {
    return <PageShell title="School Summary" eyebrow="Administrator workspace"><Section title="Loading">Opening the school summary...</Section></PageShell>;
  }

  if (!access) {
    return (
      <PageShell title="School Summary" eyebrow="Administrator workspace">
        <Section title="Administrator access required"><button type="button" onClick={() => navigate('team-directory')}><Users size={17} /> Sign in to Team Directory</button></Section>
      </PageShell>
    );
  }

  if (!hasAdministrativeAccess(access)) {
    return (
      <PageShell title="School Summary" eyebrow="Administrator workspace">
        <Section title="Administrator access required"><button type="button" onClick={() => navigate('team-directory')}><Users size={17} /> Team Directory</button></Section>
      </PageShell>
    );
  }

  if (!summary) {
    return <PageShell title="School Summary" eyebrow="Administrator workspace"><Section title="School Summary"><p className="status bad" role="alert"><AlertTriangle size={18} /> {error || 'Unable to load the school summary.'}</p></Section></PageShell>;
  }

  const { overall } = summary;
  return (
    <PageShell title="School Summary" eyebrow="Administrator workspace">
      <div className="summaryActions"><button type="button" onClick={() => navigate('team-directory')}><Users size={17} /> Team Directory</button></div>
      <Section title="Overall">
        <dl className="summaryStats">
          <SummaryMetric label="Schools" value={overall.schoolCount} />
          <SummaryMetric label="Active school accounts" value={overall.activeSchoolCount} />
          <SummaryMetric label="Inactive school accounts" value={overall.inactiveSchoolCount} />
          <SummaryMetric label="Individuals" value={overall.individualCount} />
          <SummaryMetric label="Active individuals" value={overall.activeIndividuals} />
          <SummaryMetric label="Inactive individuals" value={overall.inactiveIndividuals} />
          <SummaryMetric label="Graduated" value={overall.graduatedIndividuals} />
          <SummaryMetric label="Needs review" value={overall.needsReviewIndividuals} />
          <SummaryMetric label="Unassigned" value={overall.unassignedIndividuals} />
        </dl>
      </Section>
      <Section title={`Schools (${summary.schools.length})`}>
        <div className="tableWrap">
          <table className="summaryTable">
            <thead><tr><th>School</th><th>Team</th><th>CRP status</th><th>Active</th><th>Inactive</th><th>Graduated</th><th>Needs review</th><th>Total</th></tr></thead>
            <tbody>{summary.schools.map((school) => <tr key={school.school}>
              <td><button className="summarySchoolLink" type="button" onClick={() => navigateToDirectoryTeam(school.teamId)}>{school.school}</button></td>
              <td>{school.teamNames.join(', ')}</td><td>{school.membershipStatus}</td><td>{school.activeIndividuals}</td><td>{school.inactiveIndividuals}</td><td>{school.graduatedIndividuals}</td><td>{school.needsReviewIndividuals}</td><td>{school.individualCount}</td>
            </tr>)}</tbody>
          </table>
        </div>
      </Section>
    </PageShell>
  );
}

function LaunchOperationsPage() {
  const [access, setAccess] = useState<TeamDirectoryAccess | null>(null);
  const [events, setEvents] = useState<LaunchEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [flightCards, setFlightCards] = useState<LaunchFlightCardSummary[]>([]);
  const [selectedFlightCard, setSelectedFlightCard] = useState<LaunchFlightCardDetail | null>(null);
  const [reviewDraft, setReviewDraft] = useState<FlightReviewInput>({ flightResult: 'Not recorded', landingLocation: 'Not recorded', notes: '' });
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [queueLoading, setQueueLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const nextAccess = await getTeamDirectoryAccess();
        if (!active) return;
        setAccess(nextAccess);
        if (!nextAccess || (!hasAdministrativeAccess(nextAccess) && nextAccess.role !== 'lco')) return;
        const nextEvents = await listLaunchOperationEvents();
        if (!active) return;
        setEvents(nextEvents);
        setSelectedEventId(nextEvents.find((event) => event.isCurrent)?.id || nextEvents[0]?.id || '');
      } catch (nextError) {
        if (active) setError(nextError instanceof Error ? nextError.message : 'Unable to open Launch Operations.');
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    async function loadQueue() {
      if (!selectedEventId || !access || (!hasAdministrativeAccess(access) && access.role !== 'lco')) return;
      setQueueLoading(true);
      try {
        const result = await listLaunchFlightCards(selectedEventId, page);
        if (!active) return;
        setFlightCards(result.submissions);
        setTotal(result.total);
        setTotalPages(result.totalPages);
      } catch (nextError) {
        if (active) setError(nextError instanceof Error ? nextError.message : 'Unable to load Flight Cards.');
      } finally {
        if (active) setQueueLoading(false);
      }
    }
    void loadQueue();
    return () => { active = false; };
  }, [access, page, selectedEventId]);

  async function openFlightCard(referenceId: string) {
    if (!selectedEventId) return;
    setError('');
    setNotice('');
    try {
      const detail = await getLaunchFlightCard(selectedEventId, referenceId);
      setSelectedFlightCard(detail);
      setReviewDraft({
        flightResult: detail.review.flightResult,
        landingLocation: detail.review.landingLocation,
        notes: detail.review.flightNotes,
      });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to load the Flight Card.');
    }
  }

  async function saveFlightResult(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedEventId || !selectedFlightCard) return;
    setSaving(true);
    setError('');
    try {
      const detail = await updateFlightReview(selectedEventId, selectedFlightCard.referenceId, reviewDraft);
      setSelectedFlightCard(detail);
      setReviewDraft({ flightResult: detail.review.flightResult, landingLocation: detail.review.landingLocation, notes: detail.review.flightNotes });
      const result = await listLaunchFlightCards(selectedEventId, page);
      setFlightCards(result.submissions);
      setTotal(result.total);
      setTotalPages(result.totalPages);
      setNotice('Flight result saved.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to save the flight result.');
    } finally {
      setSaving(false);
    }
  }

  async function signOut() {
    await logoutTeamAccess();
    setAccess(null);
    setEvents([]);
    setFlightCards([]);
    setSelectedFlightCard(null);
  }

  if (loading) return <PageShell title="Launch Operations" eyebrow="LCO workspace"><Section title="Loading">Opening Flight Cards...</Section></PageShell>;
  if (!access) {
    return <PageShell title="Launch Operations" eyebrow="LCO workspace"><Section title="LCO access required"><button type="button" onClick={() => navigateToTeamDirectory(`${basePath}/launch-operations`)}><Users size={17} /> Sign in</button></Section></PageShell>;
  }
  if (!hasAdministrativeAccess(access) && access.role !== 'lco') {
    return <PageShell title="Launch Operations" eyebrow="LCO workspace"><Section title="LCO access required"><p className="status bad"><AlertTriangle size={18} /> This account does not have Launch Operations access.</p></Section></PageShell>;
  }

  const selectedEvent = events.find((event) => event.id === selectedEventId);
  return (
    <PageShell title="Launch Operations" eyebrow="LCO workspace">
      <div className="summaryActions">
        {hasAdministrativeAccess(access) ? <button type="button" onClick={() => navigate('launch-summary')}><CalendarDays size={17} /> Launch summary</button> : null}
        <button type="button" onClick={() => void signOut()}><LogOut size={17} /> Sign out</button>
      </div>
      {error ? <p className="directoryMessage status bad" role="alert"><AlertTriangle size={18} /> {error}</p> : null}
      {notice ? <p className="directoryMessage status good"><CheckCircle2 size={18} /> {notice}</p> : null}
      <Section title="Launch Weekend">
        <Field label="Launch weekend"><select value={selectedEventId} onChange={(event) => { setSelectedEventId(event.target.value); setPage(1); setSelectedFlightCard(null); }}>{events.map((event) => <option key={event.id} value={event.id}>{formatLaunchWeekend(event)}</option>)}</select></Field>
      </Section>
      <Section title={`Flight Cards (${total})`}>
        {queueLoading ? <p className="status"><ClipboardList size={18} /> Loading Flight Cards...</p> : null}
        {!queueLoading && !flightCards.length ? <p className="emptyState">No Flight Cards are attached to {selectedEvent?.name || 'this launch weekend'}.</p> : null}
        {flightCards.length ? <div className="tableWrap"><table className="summaryTable submissionTable">
          <thead><tr><th>Card</th><th>Flyer</th><th>Rocket</th><th>Submitted</th><th>Flight result</th><th>Landing</th><th></th></tr></thead>
          <tbody>{flightCards.map((card) => <tr key={card.referenceId} className={card.referenceId === selectedFlightCard?.referenceId ? 'isSelected' : ''}>
            <td>{card.formTitle}</td><td>{card.flyerName || 'Not provided'}</td><td>{card.rocketName || 'Not provided'}</td><td>{formatReviewTimestamp(card.submittedAt)}</td><td>{card.review.flightResult}</td><td>{card.review.landingLocation}</td>
            <td><button type="button" onClick={() => void openFlightCard(card.referenceId)}><FileText size={17} /> Open</button></td>
          </tr>)}</tbody>
        </table></div> : null}
        {totalPages > 1 ? <div className="summaryActions paginationActions"><button type="button" disabled={page === 1 || queueLoading} onClick={() => setPage((current) => current - 1)}>Previous</button><span>Page {page} of {totalPages}</span><button type="button" disabled={page === totalPages || queueLoading} onClick={() => setPage((current) => current + 1)}>Next</button></div> : null}
      </Section>
      {selectedFlightCard ? <>
        <Section title={`${selectedFlightCard.formTitle}: ${selectedFlightCard.flyerName || selectedFlightCard.referenceId}`}>
          <div className="submissionDetailHeader"><p>Reference: {selectedFlightCard.referenceId}</p><button className="iconButton" type="button" title="Close Flight Card" onClick={() => setSelectedFlightCard(null)}><X size={17} /></button></div>
          <dl className="submissionDetailGrid">{Object.entries(selectedFlightCard.payload).map(([key, value]) => <div key={key}><dt>{submissionFieldLabel(key)}</dt><dd>{submissionFieldValue(value)}</dd></div>)}</dl>
        </Section>
        <Section title="LCO Flight Result">
          <form className="reviewForm" onSubmit={saveFlightResult}>
            <div className="grid three">
              <Field label="Flight result"><select value={reviewDraft.flightResult} onChange={(event) => setReviewDraft({ ...reviewDraft, flightResult: event.target.value as FlightReviewInput['flightResult'] })}><option>Not recorded</option><option>Successful deployment</option><option>Main at apogee</option><option>Ballistic</option><option>Drogue / no main</option><option>Other</option></select></Field>
              <Field label="Landing location"><select value={reviewDraft.landingLocation} onChange={(event) => setReviewDraft({ ...reviewDraft, landingLocation: event.target.value as FlightReviewInput['landingLocation'] })}><option>Not recorded</option><option>On field</option><option>Off field</option></select></Field>
              <Field label="Last updated"><input value={selectedFlightCard.review.flightReviewedAt ? `${formatReviewTimestamp(selectedFlightCard.review.flightReviewedAt)} by ${selectedFlightCard.review.flightReviewedByEmail}` : 'Not recorded'} disabled /></Field>
            </div>
            <Field label="LCO notes"><textarea value={reviewDraft.notes} onChange={(event) => setReviewDraft({ ...reviewDraft, notes: event.target.value })} rows={3} /></Field>
            <div className="actionBar reviewActionBar"><button className="primaryButton" type="submit" disabled={saving}><Save size={18} /> Save flight result</button></div>
          </form>
        </Section>
        <Section title="LCO History">{!selectedFlightCard.history.length ? <p className="emptyState">No LCO results have been recorded.</p> : <ol className="reviewHistory">{selectedFlightCard.history.map((entry, index) => <li key={`${entry.createdAt}-${index}`}><strong>{reviewActionLabel(entry.action)}</strong><span>{formatReviewTimestamp(entry.createdAt)} by {entry.actorEmail}</span>{Object.keys(entry.details).length ? <span>{Object.entries(entry.details).map(([key, value]) => `${submissionFieldLabel(key)}: ${submissionFieldValue(value)}`).join(' | ')}</span> : null}</li>)}</ol>}</Section>
      </> : null}
    </PageShell>
  );
}

function LaunchDaySummaryPage() {
  const [access, setAccess] = useState<TeamDirectoryAccess | null>(null);
  const [summary, setSummary] = useState<LaunchDaySummary | null>(null);
  const [events, setEvents] = useState<LaunchEvent[]>([]);
  const [selectedEventId, setSelectedEventId] = useState('');
  const [submissions, setSubmissions] = useState<LaunchSubmissionSummary[]>([]);
  const [submissionPage, setSubmissionPage] = useState(1);
  const [submissionTotal, setSubmissionTotal] = useState(0);
  const [submissionTotalPages, setSubmissionTotalPages] = useState(1);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [selectedSubmission, setSelectedSubmission] = useState<LaunchSubmissionDetail | null>(null);
  const [submissionLoading, setSubmissionLoading] = useState(false);
  const [certificationReviewDraft, setCertificationReviewDraft] = useState<CertificationReviewInput>({ decision: 'Pending', notes: '', signerId: '', checklist: [] });
  const [signerDirectory, setSignerDirectory] = useState<CertificationSignerDirectory>({ signers: [], temporaryAuthorization: null });
  const [temporarySignerId, setTemporarySignerId] = useState('');
  const [temporaryAuthorizationNotes, setTemporaryAuthorizationNotes] = useState('');
  const [eventDraft, setEventDraft] = useState<LaunchEventInput>(emptyLaunchEvent);
  const [editingEventId, setEditingEventId] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reviewSaving, setReviewSaving] = useState(false);
  const [showAllLaunchesSummary, setShowAllLaunchesSummary] = useState(false);

  useEffect(() => {
    void load();
  }, []);

  useEffect(() => {
    let active = true;
    async function loadSubmissions() {
      if (!hasAdministrativeAccess(access) || !selectedEventId) return;
      setSubmissionsLoading(true);
      try {
        const [next, nextSigners] = await Promise.all([
          listLaunchSubmissions(selectedEventId, submissionPage),
          getCertificationSigners(selectedEventId),
        ]);
        if (!active) return;
        setSubmissions(next.submissions);
        setSubmissionTotal(next.total);
        setSubmissionTotalPages(next.totalPages);
        setSignerDirectory(nextSigners);
        setTemporarySignerId(nextSigners.temporaryAuthorization?.signerId || '');
        setTemporaryAuthorizationNotes(nextSigners.temporaryAuthorization?.notes || '');
      } catch (nextError) {
        if (active) setError(nextError instanceof Error ? nextError.message : 'Unable to load the certification submissions.');
      } finally {
        if (active) setSubmissionsLoading(false);
      }
    }
    void loadSubmissions();
    return () => { active = false; };
  }, [access?.role, selectedEventId, submissionPage]);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const nextAccess = await getTeamDirectoryAccess();
      setAccess(nextAccess);
      if (!hasAdministrativeAccess(nextAccess)) return;
      const [nextSummary, nextEvents] = await Promise.all([getLaunchDaySummary(), listLaunchEvents()]);
      setSummary(nextSummary);
      setEvents(nextEvents);
      setSelectedEventId((current) => {
        if (current && nextSummary.events.some((event) => event.id === current)) return current;
        return nextSummary.events.find((event) => event.isCurrent)?.id || nextSummary.events[0]?.id || '';
      });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to load the launch summary.');
    } finally {
      setLoading(false);
    }
  }

  function chooseEvent(eventId: string) {
    setSelectedEventId(eventId);
    setSubmissionPage(1);
    setSelectedSubmission(null);
    setNotice('');
  }

  async function openSubmission(referenceId: string) {
    if (!selectedEventId) return;
    setSubmissionLoading(true);
    setError('');
    setNotice('');
    try {
      const detail = await getLaunchSubmission(selectedEventId, referenceId);
      setSelectedSubmission(detail);
      setCertificationReviewDraft({
        decision: detail.review.certificationDecision,
        notes: detail.review.certificationNotes,
        signerId: detail.review.certificationSignerId,
        checklist: detail.review.certificationChecklist.map(({ id, status, notes }) => ({ id, status, notes })),
      });
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to load the flight submission.');
    } finally {
      setSubmissionLoading(false);
    }
  }

  async function saveCertificationDecision(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedEventId || !selectedSubmission) return;
    setReviewSaving(true);
    setError('');
    try {
      const detail = await updateCertificationReview(selectedEventId, selectedSubmission.referenceId, certificationReviewDraft);
      setSelectedSubmission(detail);
      setCertificationReviewDraft({
        decision: detail.review.certificationDecision,
        notes: detail.review.certificationNotes,
        signerId: detail.review.certificationSignerId,
        checklist: detail.review.certificationChecklist.map(({ id, status, notes }) => ({ id, status, notes })),
      });
      if (detail.review.certificationDecision === 'Approved' && detail.review.signedFormAvailable) {
        setNotice(detail.review.signedFormDeliveryStatus === 'Delivered'
          ? 'Certification approved. The signed official form was archived and emailed to the flyer.'
          : 'Certification approved and the signed official form was archived. Email delivery failed; the form remains available here.');
      } else {
        setNotice('Certification decision saved.');
      }
      const next = await listLaunchSubmissions(selectedEventId, submissionPage);
      setSubmissions(next.submissions);
      setSubmissionTotal(next.total);
      setSubmissionTotalPages(next.totalPages);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to save the certification decision.');
    } finally {
      setReviewSaving(false);
    }
  }

  async function resendSignedForm() {
    if (!selectedEventId || !selectedSubmission) return;
    setReviewSaving(true);
    setError('');
    setNotice('');
    try {
      const detail = await resendSignedCertificationForm(selectedEventId, selectedSubmission.referenceId);
      setSelectedSubmission(detail);
      if (detail.review.signedFormDeliveryStatus === 'Delivered') {
        setNotice('The signed certification form was emailed to the flyer and copied to URRG.');
      } else {
        setError('The signed form is still archived, but email delivery failed.');
      }
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to resend the signed certification form.');
    } finally {
      setReviewSaving(false);
    }
  }

  function approveAllCertificationItems() {
    setCertificationReviewDraft((current) => ({
      ...current,
      decision: 'Approved',
      checklist: current.checklist.map((item) => ({ ...item, status: 'Approved' })),
    }));
  }

  function updateCertificationChecklistItem(itemId: string, updates: Partial<CertificationChecklistItemInput>) {
    setCertificationReviewDraft((current) => {
      const checklist = current.checklist.map((item) => item.id === itemId ? { ...item, ...updates } : item);
      const decision = updates.status === 'Failed'
        ? 'Failed'
        : current.decision === 'Approved' && updates.status && !['Approved', 'N/A'].includes(updates.status)
          ? 'Pending'
          : current.decision;
      return { ...current, decision, checklist };
    });
  }

  async function saveTemporaryAuthorization(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedEventId || !temporarySignerId) {
      setError('Choose the person receiving temporary certification authorization.');
      return;
    }
    setReviewSaving(true);
    setError('');
    try {
      const next = await saveTemporaryCertificationAuthorization(selectedEventId, temporarySignerId, temporaryAuthorizationNotes);
      setSignerDirectory(next);
      setTemporarySignerId(next.temporaryAuthorization?.signerId || '');
      setTemporaryAuthorizationNotes(next.temporaryAuthorization?.notes || '');
      setNotice('Temporary certification authorization saved for this launch weekend.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to save temporary certification authorization.');
    } finally {
      setReviewSaving(false);
    }
  }

  async function clearTemporaryAuthorization() {
    if (!selectedEventId || !window.confirm('Remove the temporary certification authorization for this launch weekend?')) return;
    setReviewSaving(true);
    setError('');
    try {
      await removeTemporaryCertificationAuthorization(selectedEventId);
      const next = await getCertificationSigners(selectedEventId);
      setSignerDirectory(next);
      setTemporarySignerId('');
      setTemporaryAuthorizationNotes('');
      setNotice('Temporary certification authorization removed.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to remove temporary certification authorization.');
    } finally {
      setReviewSaving(false);
    }
  }

  function startEventEdit(event: LaunchEvent) {
    setEditingEventId(event.id);
    setEventDraft({
      name: event.name,
      startDate: event.startDate,
      endDate: event.endDate,
      status: event.status,
      acceptingSubmissions: event.acceptingSubmissions,
    });
  }

  function startNewEvent() {
    setEditingEventId('');
    setEventDraft(emptyLaunchEvent());
  }

  async function saveEvent(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSaving(true);
    setError('');
    try {
      if (editingEventId) await updateLaunchEvent(editingEventId, eventDraft);
      else await createLaunchEvent(eventDraft);
      startNewEvent();
      await load();
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to save the launch weekend.');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return <PageShell title="Launch Day Summary" eyebrow="Administrator workspace"><Section title="Loading">Opening the launch summary...</Section></PageShell>;
  }

  if (!access) {
    return (
      <PageShell title="Launch Day Summary" eyebrow="Administrator workspace">
        <Section title="Administrator access required"><button type="button" onClick={() => navigate('team-directory')}><Users size={17} /> Sign in to Team Directory</button></Section>
      </PageShell>
    );
  }

  if (!hasAdministrativeAccess(access)) {
    return (
      <PageShell title="Launch Day Summary" eyebrow="Administrator workspace">
        <Section title="Administrator access required"><button type="button" onClick={() => navigate('team-directory')}><Users size={17} /> Team Directory</button></Section>
      </PageShell>
    );
  }

  if (!summary) {
    return <PageShell title="Launch Day Summary" eyebrow="Administrator workspace"><Section title="Launch Day Summary"><p className="status bad" role="alert"><AlertTriangle size={18} /> {error || 'Unable to load the launch summary.'}</p></Section></PageShell>;
  }

  const selectedEvent = summary.events.find((event) => event.id === selectedEventId) || summary.events[0];
  const { overall } = summary;
  const eligibleSelectedSigners = selectedSubmission
    ? signerDirectory.signers.filter((signer) => signer.effectiveCapabilities.includes(selectedSubmission.requiredCapability))
    : [];
  const selectedSignerProfile = signerDirectory.signers.find((signer) => signer.id === certificationReviewDraft.signerId);
  const selectedCertificationOrganization = String(selectedSubmission?.payload.certOrg || '').toUpperCase();
  const selectedSignerReady = !selectedSignerProfile
    ? false
    : selectedSignerProfile.hasSignature && (
      selectedCertificationOrganization === 'NAR'
        ? Boolean(selectedSignerProfile.email && selectedSignerProfile.narNumber && selectedSignerProfile.narExpiration && selectedSignerProfile.certificationLevel)
        : Boolean(selectedSignerProfile.traNumber)
    );

  return (
    <PageShell title="Launch Day Summary" eyebrow="Administrator workspace">
      <div className="summaryActions">
        <button type="button" onClick={() => navigate('launch-operations')}><ClipboardList size={17} /> Launch Operations</button>
        <button type="button" onClick={() => navigate('admins')}><ShieldCheck size={17} /> Administrators</button>
        <button type="button" onClick={() => navigate('team-directory')}><Users size={17} /> Team Directory</button>
      </div>
      {error ? <p className="directoryMessage status bad" role="alert"><AlertTriangle size={18} /> {error}</p> : null}
      {notice ? <p className="directoryMessage status good"><CheckCircle2 size={18} /> {notice}</p> : null}
      <Section title="Selected Launch Weekend">
        <p className="launchSummaryLead">{selectedEvent ? formatLaunchWeekend(selectedEvent) : 'Select a launch weekend below.'}</p>
        {selectedEvent ? <dl className="summaryStats">
          <SummaryMetric label="Submitted flight forms" value={selectedEvent.flightSubmissions} />
          <SummaryMetric label="Standalone flight cards" value={selectedEvent.standaloneFlightCards} />
          <SummaryMetric label="Project flight forms" value={selectedEvent.projectFlights} />
          <SummaryMetric label="Certification forms" value={selectedEvent.certificationFlights} />
          <SummaryMetric label="L1 certifications" value={selectedEvent.l1Certifications} />
          <SummaryMetric label="L2 certifications" value={selectedEvent.l2Certifications} />
          <SummaryMetric label="L3 certifications" value={selectedEvent.l3Certifications} />
          <SummaryMetric label="Team manifests" value={selectedEvent.manifests} />
          <SummaryMetric label="Manifest flyers" value={selectedEvent.manifestFlyers} />
          <SummaryMetric label="Spectators / non-flyers" value={selectedEvent.spectators} />
          <SummaryMetric label="Manifest L1 plans" value={selectedEvent.manifestL1CertFlights} />
          <SummaryMetric label="Manifest L2 plans" value={selectedEvent.manifestL2CertFlights} />
          <SummaryMetric label="Manifest project plans" value={selectedEvent.manifestProjectFlights} />
          <SummaryMetric label="Manifest standard flights" value={selectedEvent.manifestStandardFlights} />
        </dl> : null}
      </Section>
      <div className="summaryActions summaryDisclosure">
        <button type="button" onClick={() => setShowAllLaunchesSummary((visible) => !visible)}>
          {showAllLaunchesSummary ? <ChevronUp size={17} /> : <ChevronDown size={17} />}
          {showAllLaunchesSummary ? 'Hide All Scheduled Launches Summary' : 'View All Scheduled Launches Summary'}
        </button>
      </div>
      {showAllLaunchesSummary ? <Section title="All Scheduled Launches Summary">
        <dl className="summaryStats">
          <SummaryMetric label="Launch weekends" value={overall.launchEvents} />
          <SummaryMetric label="Flight submissions" value={overall.flightSubmissions} />
          <SummaryMetric label="Certification forms" value={overall.certificationFlights} />
          <SummaryMetric label="Project flights" value={overall.projectFlights} />
          <SummaryMetric label="Manifest flyers" value={overall.manifestFlyers} />
          <SummaryMetric label="Spectators / non-flyers" value={overall.spectators} />
          <SummaryMetric label="Team manifests" value={overall.manifests} />
          <SummaryMetric label="Unassigned older forms" value={overall.unassignedSubmissions} />
        </dl>
      </Section> : null}
      <Section title="Launch Weekends">
        <div className="tableWrap">
          <table className="summaryTable launchSummaryTable">
            <thead><tr><th>Launch weekend</th><th>Status</th><th>Current</th><th>Flight forms</th><th>L1</th><th>L2</th><th>L3</th><th>Project</th><th>Flyers</th><th>Spectators</th><th></th></tr></thead>
            <tbody>{summary.events.map((event) => <tr key={event.id} className={event.id === selectedEvent?.id ? 'isSelected' : ''}>
              <td><button className="summarySchoolLink" type="button" onClick={() => chooseEvent(event.id)}>{formatLaunchWeekend(event)}</button></td>
              <td>{event.status}</td><td>{event.isCurrent ? 'Yes' : 'No'}</td><td>{event.flightSubmissions}</td><td>{event.l1Certifications}</td><td>{event.l2Certifications}</td><td>{event.l3Certifications}</td><td>{event.projectFlights}</td><td>{event.manifestFlyers}</td><td>{event.spectators}</td>
              <td><button className="iconButton" type="button" title={`Edit ${event.name}`} onClick={() => startEventEdit(event)}><Save size={17} /></button></td>
            </tr>)}</tbody>
          </table>
        </div>
      </Section>
      {selectedEvent ? <>
        <Section title="Certification Signers">
          <div className="sectionHeadingActions"><button type="button" onClick={() => navigate('admins')}><ShieldCheck size={17} /> Manage signer profiles and signatures</button></div>
          <div className="tableWrap">
            <table className="summaryTable">
              <thead><tr><th>Signer</th><th>Qualifications</th><th>Permanent authority</th><th>Authority this weekend</th><th>Signature</th></tr></thead>
              <tbody>{signerDirectory.signers.map((signer) => <tr key={signer.id}>
                <td>{signer.fullName}{signer.temporaryPrefect ? <><br /><span className="tableSubtext">Temporary acting prefect</span></> : null}</td>
                <td>{signer.qualifications || 'Not recorded'}</td>
                <td>{signer.permanentCapabilities.join(', ') || 'None recorded'}</td>
                <td>{signer.effectiveCapabilities.join(', ') || 'None recorded'}</td>
                <td>{signer.hasSignature ? (signer.signatureType === 'uploaded' ? 'Image added' : 'Script facsimile') : 'Not added'}</td>
              </tr>)}</tbody>
            </table>
          </div>
          <form className="reviewForm" onSubmit={saveTemporaryAuthorization}>
            <div className="grid two">
              <Field label="Temporary acting prefect"><select value={temporarySignerId} onChange={(event) => setTemporarySignerId(event.target.value)}><option value="">None selected</option>{signerDirectory.signers.map((signer) => <option key={signer.id} value={signer.id}>{signer.fullName}</option>)}</select></Field>
              <Field label="Authorization notes"><input value={temporaryAuthorizationNotes} onChange={(event) => setTemporaryAuthorizationNotes(event.target.value)} /></Field>
            </div>
            {signerDirectory.temporaryAuthorization ? <p className="status">Authorized by {signerDirectory.temporaryAuthorization.authorizedByEmail} on {formatReviewTimestamp(signerDirectory.temporaryAuthorization.updatedAt)}.</p> : null}
            <div className="actionBar reviewActionBar"><button type="submit" disabled={reviewSaving || !temporarySignerId}><Save size={18} /> Save temporary authorization</button>{signerDirectory.temporaryAuthorization ? <button type="button" className="danger" disabled={reviewSaving} onClick={() => void clearTemporaryAuthorization()}><Trash2 size={18} /> Remove authorization</button> : null}</div>
          </form>
        </Section>
        <Section title={`Certification Submissions (${submissionTotal})`}>
          {submissionsLoading ? <p className="status"><ClipboardList size={18} /> Loading certification submissions...</p> : null}
          {!submissionsLoading && !submissions.length ? <p className="emptyState">No Certification Flights are attached to this launch weekend.</p> : null}
          {submissions.length ? <div className="tableWrap">
            <table className="summaryTable submissionTable">
              <thead><tr><th>Certification</th><th>Flyer</th><th>Rocket</th><th>Submitted</th><th>LCO result</th><th>Landing</th><th>Decision</th><th></th></tr></thead>
              <tbody>{submissions.map((submission) => <tr key={submission.referenceId} className={submission.referenceId === selectedSubmission?.referenceId ? 'isSelected' : ''}>
                <td>{submission.certAttempt || 'Not provided'}<br /><span className="tableSubtext">{submission.requiredCapability}</span></td>
                <td>{submission.flyerName || 'Not provided'}<br /><span className="tableSubtext">{submission.flyerEmail || 'No email'}</span></td>
                <td>{submission.rocketName || 'Not provided'}</td>
                <td>{formatReviewTimestamp(submission.submittedAt)}</td>
                <td>{submission.review.flightResult}</td>
                <td>{submission.review.landingLocation}</td>
                <td>{submission.review.certificationDecision === 'Pending' ? 'Not yet recorded' : submission.review.certificationDecision}</td>
                <td className="rowActions"><button type="button" onClick={() => void openSubmission(submission.referenceId)} disabled={submissionLoading}><FileText size={17} /> View</button>{submission.review.signedFormAvailable ? <button type="button" onClick={() => window.open(signedCertificationFormUrl(selectedEvent.id, submission.referenceId), '_blank', 'noopener,noreferrer')}><Download size={17} /> View signed form</button> : submission.review.certificationDecision === 'Approved' ? <span className="tableSubtext">Signed form pending</span> : null}</td>
              </tr>)}</tbody>
            </table>
          </div> : null}
          {submissionTotalPages > 1 ? <div className="summaryActions paginationActions"><button type="button" disabled={submissionPage === 1 || submissionsLoading} onClick={() => setSubmissionPage((page) => page - 1)}>Previous</button><span>Page {submissionPage} of {submissionTotalPages}</span><button type="button" disabled={submissionPage === submissionTotalPages || submissionsLoading} onClick={() => setSubmissionPage((page) => page + 1)}>Next</button></div> : null}
        </Section>
        {selectedSubmission ? <>
          <Section title={`${selectedSubmission.formTitle}: ${selectedSubmission.flyerName || selectedSubmission.referenceId}`}>
            <div className="submissionDetailHeader">
              <p>Reference: {selectedSubmission.referenceId} | Result: {selectedSubmission.review.certificationDecision === 'Pending' ? 'Not yet recorded' : selectedSubmission.review.certificationDecision} | Required signer authority: {selectedSubmission.requiredCapability}</p>
              <div className="rowActions">{selectedSubmission.review.signedFormAvailable ? <button type="button" onClick={() => window.open(signedCertificationFormUrl(selectedEvent.id, selectedSubmission.referenceId), '_blank', 'noopener,noreferrer')}><Download size={17} /> View signed form</button> : null}<button className="iconButton" type="button" title="Close certification details" onClick={() => setSelectedSubmission(null)}><X size={17} /></button></div>
            </div>
            {selectedSubmission.review.signedFormAvailable ? <div className="signedFormStatus"><p className="status">Signed form: {selectedSubmission.review.signedFormFilename} | Email: {selectedSubmission.review.signedFormDeliveryStatus || 'Pending'}{selectedSubmission.review.signedFormCurrent ? '' : ' | Superseded by the current decision'}</p>{selectedSubmission.review.signedFormCurrent && selectedSubmission.review.signedFormDeliveryStatus !== 'Delivered' ? <button type="button" disabled={reviewSaving} onClick={() => void resendSignedForm()}><Upload size={17} /> Resend signed form</button> : null}</div> : null}
            {selectedSubmission.review.certificationDecision === 'Approved' && !selectedSubmission.review.signedFormAvailable ? <p className="status bad"><AlertTriangle size={18} /> Signed form pending. Complete the signer profile and signature, then save this approval again.</p> : null}
            <dl className="submissionDetailGrid">
              {Object.entries(selectedSubmission.payload).map(([key, value]) => <div key={key}><dt>{submissionFieldLabel(key)}</dt><dd>{submissionFieldValue(value)}</dd></div>)}
            </dl>
          </Section>
          <Section title="LCO Result">
            <dl className="submissionDetailGrid">
              <div><dt>Flight result</dt><dd>{selectedSubmission.review.flightResult}</dd></div>
              <div><dt>Landing location</dt><dd>{selectedSubmission.review.landingLocation}</dd></div>
              <div><dt>LCO notes</dt><dd>{selectedSubmission.review.flightNotes || 'None recorded'}</dd></div>
              <div><dt>Last updated</dt><dd>{selectedSubmission.review.flightReviewedAt ? `${formatReviewTimestamp(selectedSubmission.review.flightReviewedAt)} by ${selectedSubmission.review.flightReviewedByEmail}` : 'Not recorded'}</dd></div>
            </dl>
          </Section>
          <Section title="Certification Review">
            <form className="reviewForm" onSubmit={saveCertificationDecision}>
              <div className="actionBar certificationApproveAll"><button type="button" className="primaryButton" disabled={reviewSaving || !certificationReviewDraft.checklist.length} onClick={approveAllCertificationItems}><CheckCircle2 size={18} /> Approve All</button></div>
              <div className="certificationChecklist">
                {Array.from(new Set(selectedSubmission.review.certificationChecklist.map((item) => item.section))).map((section) => <div className="certificationChecklistSection" key={section}>
                  <h3>{section}</h3>
                  <div className="tableWrap"><table className="summaryTable certificationChecklistTable">
                    <thead><tr><th>Item</th><th>Result</th><th>Notes</th></tr></thead>
                    <tbody>{selectedSubmission.review.certificationChecklist.filter((item) => item.section === section).map((item) => {
                      const draft = certificationReviewDraft.checklist.find((entry) => entry.id === item.id) || { id: item.id, status: 'Pending' as CertificationChecklistStatus, notes: '' };
                      return <tr key={item.id}>
                        <td>{item.label}</td>
                        <td><select aria-label={`${item.label} result`} value={draft.status} onChange={(event) => updateCertificationChecklistItem(item.id, { status: event.target.value as CertificationChecklistStatus })}><option>Pending</option><option>Approved</option><option>Failed</option><option>N/A</option></select></td>
                        <td><input aria-label={`${item.label} notes`} value={draft.notes} onChange={(event) => updateCertificationChecklistItem(item.id, { notes: event.target.value })} /></td>
                      </tr>;
                    })}</tbody>
                  </table></div>
                </div>)}
              </div>
              <div className="grid three">
                <Field label="Certification decision"><select value={certificationReviewDraft.decision} onChange={(event) => setCertificationReviewDraft({ ...certificationReviewDraft, decision: event.target.value as CertificationReviewInput['decision'] })}><option>Pending</option><option>Approved</option><option>Failed</option><option>Incomplete</option><option>Not attempted</option></select></Field>
                <Field label={`Authorized signer (${selectedSubmission.requiredCapability})`}><select value={certificationReviewDraft.signerId} required={certificationReviewDraft.decision === 'Approved'} onChange={(event) => setCertificationReviewDraft({ ...certificationReviewDraft, signerId: event.target.value })}><option value="">Choose signer</option>{eligibleSelectedSigners.map((signer) => <option key={signer.id} value={signer.id}>{signer.fullName}{signer.temporaryPrefect ? ' (temporary)' : ''}{signer.hasSignature ? '' : ' (signature needed)'}</option>)}</select></Field>
                <Field label="Last updated"><input value={selectedSubmission.review.certificationDecidedAt ? `${formatReviewTimestamp(selectedSubmission.review.certificationDecidedAt)} by ${selectedSubmission.review.certificationDecidedByEmail}` : 'Not recorded'} disabled /></Field>
              </div>
              {certificationReviewDraft.decision === 'Approved' && !eligibleSelectedSigners.length ? <p className="status bad"><AlertTriangle size={18} /> No active signer has {selectedSubmission.requiredCapability} authority for this launch weekend.</p> : null}
              {certificationReviewDraft.decision === 'Approved' && certificationReviewDraft.signerId && !selectedSignerReady ? <p className="status bad"><AlertTriangle size={18} /> This signer needs a signature and complete {selectedCertificationOrganization || 'authority'} profile in Administrator Management before approval.</p> : null}
              {selectedSubmission.review.certificationSignerName ? <p className="status">Recorded signer: {selectedSubmission.review.certificationSignerName}</p> : null}
              <Field label="Certification notes"><textarea value={certificationReviewDraft.notes} onChange={(event) => setCertificationReviewDraft({ ...certificationReviewDraft, notes: event.target.value })} rows={3} /></Field>
              <div className="actionBar reviewActionBar"><button className="primaryButton" type="submit" disabled={reviewSaving || (certificationReviewDraft.decision === 'Approved' && !selectedSignerReady)}><CheckCircle2 size={18} /> Save certification decision</button></div>
            </form>
          </Section>
          <Section title="Certification History">
            {!selectedSubmission.history.length ? <p className="emptyState">No certification decisions have been recorded.</p> : <ol className="reviewHistory">{selectedSubmission.history.map((entry, index) => <li key={`${entry.createdAt}-${index}`}><strong>{reviewActionLabel(entry.action)}</strong><span>{formatReviewTimestamp(entry.createdAt)} by {entry.actorEmail}</span>{Object.keys(entry.details).length ? <span>{Object.entries(entry.details).map(([key, value]) => `${submissionFieldLabel(key)}: ${submissionFieldValue(value)}`).join(' | ')}</span> : null}</li>)}</ol>}
          </Section>
        </> : null}
      </> : null}
      <Section title={editingEventId ? 'Edit Launch Weekend' : 'Add Launch Weekend'}>
        <form className="launchEventForm" onSubmit={saveEvent}>
          <div className="grid four">
            <Field label="Launch name" required><input value={eventDraft.name} onChange={(event) => setEventDraft({ ...eventDraft, name: event.target.value })} required /></Field>
            <Field label="Start date" required><input type="date" value={eventDraft.startDate} onChange={(event) => setEventDraft({ ...eventDraft, startDate: event.target.value })} required /></Field>
            <Field label="End date" required><input type="date" value={eventDraft.endDate} onChange={(event) => setEventDraft({ ...eventDraft, endDate: event.target.value })} required /></Field>
            <Field label="Status"><select value={eventDraft.status} onChange={(event) => setEventDraft({ ...eventDraft, status: event.target.value as LaunchEventInput['status'] })}><option>Scheduled</option><option>Moved</option><option>Completed</option><option>Cancelled</option></select></Field>
          </div>
          <div className="actionBar"><button className="primaryButton" type="submit" disabled={saving}><Save size={18} /> {editingEventId ? 'Save launch weekend' : 'Add launch weekend'}</button>{editingEventId ? <button type="button" disabled={saving} onClick={startNewEvent}>Cancel</button> : null}</div>
        </form>
      </Section>
    </PageShell>
  );
}

function AdminManagementPage() {
  const [access, setAccess] = useState<TeamDirectoryAccess | null>(null);
  const [admins, setAdmins] = useState<AdminUser[]>([]);
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  const [signers, setSigners] = useState<CertificationSigner[]>([]);
  const [selectedSignerId, setSelectedSignerId] = useState('');
  const [signerDraft, setSignerDraft] = useState<CertificationSignerProfileInput>(certificationSignerProfileDraft());
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const signerFileInput = useRef<HTMLInputElement | null>(null);
  const selectedSigner = signers.find((signer) => signer.id === selectedSignerId);

  useEffect(() => {
    let active = true;
    async function load() {
      try {
        const nextAccess = await getTeamDirectoryAccess();
        if (!active) return;
        setAccess(nextAccess);
        if (hasAdministrativeAccess(nextAccess)) {
          const [accessDirectory, nextSigners] = await Promise.all([listAccessUsers(), listCertificationSignerProfiles()]);
          if (!active) return;
          setAdmins(accessDirectory.admins);
          setBoardMembers(accessDirectory.boardMembers);
          setSigners(nextSigners);
          if (nextSigners[0]) {
            setSelectedSignerId(nextSigners[0].id);
            setSignerDraft(certificationSignerProfileDraft(nextSigners[0]));
          }
        }
      } catch (nextError) {
        if (active) setError(nextError instanceof Error ? nextError.message : 'Unable to load administrator access.');
      } finally {
        if (active) setLoading(false);
      }
    }
    void load();
    return () => { active = false; };
  }, []);

  function selectSigner(signerId: string) {
    const signer = signers.find((item) => item.id === signerId);
    setSelectedSignerId(signerId);
    setSignerDraft(certificationSignerProfileDraft(signer));
    if (signerFileInput.current) signerFileInput.current.value = '';
  }

  function replaceSigner(updated: CertificationSigner) {
    setSigners((current) => current.map((signer) => signer.id === updated.id ? updated : signer));
    setSelectedSignerId(updated.id);
    setSignerDraft(certificationSignerProfileDraft(updated));
  }

  async function saveSignerProfile(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedSignerId) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      replaceSigner(await updateCertificationSignerProfile(selectedSignerId, signerDraft));
      setNotice('Certification signer profile saved.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to save the certification signer profile.');
    } finally {
      setBusy(false);
    }
  }

  async function useSignerFacsimile() {
    if (!selectedSignerId) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      replaceSigner(await useCertificationSignerFacsimile(selectedSignerId));
      setNotice('Script facsimile signature added.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to add the facsimile signature.');
    } finally {
      setBusy(false);
    }
  }

  async function uploadSignerSignature() {
    const file = signerFileInput.current?.files?.[0];
    if (!selectedSignerId || !file) {
      setError('Choose a JPG or PNG signature image first.');
      return;
    }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      replaceSigner(await uploadCertificationSignerSignature(selectedSignerId, file));
      if (signerFileInput.current) signerFileInput.current.value = '';
      setNotice('Signature image added.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to upload the signature image.');
    } finally {
      setBusy(false);
    }
  }

  async function removeSignerSignature() {
    if (!selectedSignerId || !window.confirm(`Remove ${selectedSigner?.fullName || 'this signer'}'s stored signature?`)) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await deleteCertificationSignerSignature(selectedSignerId);
      const nextSigners = await listCertificationSignerProfiles();
      setSigners(nextSigners);
      const updated = nextSigners.find((signer) => signer.id === selectedSignerId);
      setSignerDraft(certificationSignerProfileDraft(updated));
      setNotice('Stored signature removed.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to remove the stored signature.');
    } finally {
      setBusy(false);
    }
  }

  async function grantAccess(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const created = await createAdminUser(email);
      const accessDirectory = await listAccessUsers();
      setAdmins(accessDirectory.admins);
      setBoardMembers(accessDirectory.boardMembers);
      setEmail('');
      setNotice(`${created.email} now has URRG administrator access.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to grant administrator access.');
    } finally {
      setBusy(false);
    }
  }

  async function removeAccess(admin: AdminUser) {
    if (!admin.removable || !window.confirm(`Remove URRG administrator access for ${admin.email}?`)) return;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await deleteAdminUser(admin.id);
      const accessDirectory = await listAccessUsers();
      setAdmins(accessDirectory.admins);
      setBoardMembers(accessDirectory.boardMembers);
      setNotice(`${admin.email} no longer has managed administrator access.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to remove administrator access.');
    } finally {
      setBusy(false);
    }
  }

  async function signOut() {
    setBusy(true);
    try {
      await logoutTeamAccess();
      setAccess(null);
      setAdmins([]);
      setBoardMembers([]);
      setSigners([]);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to sign out.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) return <PageShell title="Administrator Management" eyebrow="URRG administrator"><Section title="Loading">Opening administrator access...</Section></PageShell>;
  if (!access) return <PageShell title="Administrator Management" eyebrow="URRG administrator"><Section title="Administrator access required"><button type="button" onClick={() => navigateToTeamDirectory(`${basePath}/admins`)}><ShieldCheck size={17} /> Sign in</button></Section></PageShell>;
  if (!hasAdministrativeAccess(access)) return <PageShell title="Administrator Management" eyebrow="URRG administrator"><Section title="Administrator access required"><p className="status bad"><AlertTriangle size={18} /> This account does not have administrator access.</p><button type="button" onClick={() => navigate('team-directory')}><Users size={17} /> Team Directory</button></Section></PageShell>;

  return (
    <PageShell title="Administrator Management" eyebrow={access.isSuperAdmin ? 'URRG superadmin' : access.role === 'board' ? 'URRG board member' : 'URRG administrator'}>
      <div className="summaryActions">
        <button type="button" onClick={() => navigate('team-directory')}><Users size={17} /> Team Directory</button>
        <button type="button" onClick={() => navigate('launch-summary')}><CalendarDays size={17} /> Launch summary</button>
        <button type="button" onClick={() => void signOut()} disabled={busy}><LogOut size={17} /> Sign out</button>
      </div>
      {error ? <p className="directoryMessage status bad" role="alert"><AlertTriangle size={18} /> {error}</p> : null}
      {notice ? <p className="directoryMessage status good"><CheckCircle2 size={18} /> {notice}</p> : null}
      <Section title="Certification Signers">
        <div className="tableWrap"><table className="summaryTable signerAdminTable">
          <thead><tr><th>Signer</th><th>Authority</th><th>NAR</th><th>TRA</th><th>Signature</th></tr></thead>
          <tbody>{signers.map((signer) => <tr key={signer.id} className={signer.id === selectedSignerId ? 'isSelected' : ''}>
            <td><button className="summarySchoolLink" type="button" onClick={() => selectSigner(signer.id)}>{signer.fullName}</button><br /><span className="tableSubtext">{signer.email || 'No email recorded'}</span></td>
            <td>{signer.permanentCapabilities.join(', ') || 'None recorded'}</td>
            <td>{signer.narNumber || '-'}{signer.narExpiration ? <><br /><span className="tableSubtext">Expires {signer.narExpiration}</span></> : null}</td>
            <td>{signer.traNumber || '-'}</td>
            <td>{signer.hasSignature ? (signer.signatureType === 'uploaded' ? 'Image' : 'Script facsimile') : 'Not added'}</td>
          </tr>)}</tbody>
        </table></div>
        {access.isSuperAdmin && selectedSigner ? <form className="reviewForm signerProfileForm" onSubmit={saveSignerProfile}>
          <h3>{selectedSigner.fullName}</h3>
          <div className="grid four">
            <Field label="Signer email"><input type="email" value={signerDraft.email} onChange={(event) => setSignerDraft({ ...signerDraft, email: event.target.value })} /></Field>
            <Field label="NAR number"><input value={signerDraft.narNumber} onChange={(event) => setSignerDraft({ ...signerDraft, narNumber: event.target.value })} /></Field>
            <Field label="NAR expiration"><input type="date" value={signerDraft.narExpiration} onChange={(event) => setSignerDraft({ ...signerDraft, narExpiration: event.target.value })} /></Field>
            <Field label="TRA number"><input value={signerDraft.traNumber} onChange={(event) => setSignerDraft({ ...signerDraft, traNumber: event.target.value })} /></Field>
            <Field label="Certification level"><select value={signerDraft.certificationLevel} onChange={(event) => setSignerDraft({ ...signerDraft, certificationLevel: event.target.value })}><option value="">Not recorded</option><option>L1</option><option>L2</option><option>L3</option></select></Field>
            <Field label="Signature"><input value={selectedSigner.hasSignature ? (selectedSigner.signatureType === 'uploaded' ? 'Signature image' : 'Script facsimile') : 'Not added'} disabled /></Field>
          </div>
          <div className="actionBar"><button className="primaryButton" type="submit" disabled={busy}><Save size={17} /> Save signer profile</button></div>
          <div className="signatureControls">
            <Field label="Signature image"><input ref={signerFileInput} type="file" accept="image/png,image/jpeg" disabled={busy} /></Field>
            <div className="actionBar"><button type="button" disabled={busy} onClick={() => void uploadSignerSignature()}><Upload size={17} /> Upload signature</button><button type="button" disabled={busy} onClick={() => void useSignerFacsimile()}><FileText size={17} /> Use script facsimile</button>{selectedSigner.hasSignature ? <button className="danger" type="button" disabled={busy} onClick={() => void removeSignerSignature()}><Trash2 size={17} /> Remove signature</button> : null}</div>
          </div>
        </form> : null}
      </Section>
      {access.isSuperAdmin ? <Section title="Grant Administrator Access">
        <form className="accessForm" onSubmit={grantAccess}>
          <Field label="Administrator email" required><input type="email" value={email} onChange={(event) => setEmail(event.target.value)} required autoComplete="email" /></Field>
          <button className="primaryButton" type="submit" disabled={busy}><UserPlus size={17} /> Grant access</button>
        </form>
      </Section> : null}
      <Section title={`Administrators (${admins.length})`}>
        <div className="tableWrap"><table className="summaryTable adminTable">
          <thead><tr><th>Email</th><th>Access source</th><th>Added by</th><th>Added</th><th></th></tr></thead>
          <tbody>{admins.map((admin) => <tr key={admin.email}>
            <td>{admin.email}</td>
            <td>{admin.superAdmin ? 'Superadmin' : admin.configured ? 'Server configuration' : 'Managed'}</td>
            <td>{admin.createdByEmail || '-'}</td>
            <td>{admin.createdAt ? formatReviewTimestamp(admin.createdAt) : '-'}</td>
            <td>{admin.removable ? <button className="iconButton danger" type="button" title={`Remove ${admin.email}`} disabled={busy} onClick={() => void removeAccess(admin)}><Trash2 size={16} /></button> : <span className="tableSubtext">{admin.superAdmin ? 'Superadmin' : 'Protected'}</span>}</td>
          </tr>)}</tbody>
        </table></div>
      </Section>
      <Section title={`Board Members (${boardMembers.length})`}>
        <div className="tableWrap"><table className="summaryTable adminTable">
          <thead><tr><th>Email</th><th>Role</th><th>Access source</th></tr></thead>
          <tbody>{boardMembers.map((member) => <tr key={member.email}>
            <td>{member.email}</td>
            <td>Board member</td>
            <td>{member.configured ? 'Server configuration' : 'Managed'}</td>
          </tr>)}</tbody>
        </table></div>
      </Section>
    </PageShell>
  );
}

function TeamDirectoryPage() {
  const [access, setAccess] = useState<TeamDirectoryAccess | null>(null);
  const [turnstileSiteKey, setTurnstileSiteKey] = useState('');
  const [turnstileToken, setTurnstileToken] = useState('');
  const [accessEmail, setAccessEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [teams, setTeams] = useState<TeamAccount[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState('');
  const [teamDraft, setTeamDraft] = useState<TeamAccountInput>(emptyTeamAccount);
  const [additionalLeadDraft, setAdditionalLeadDraft] = useState<(TeamAdditionalLeadInput & { id?: string }) | null>(null);
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [memberDraft, setMemberDraft] = useState<(TeamMemberInput & { id?: string }) | null>(null);
  const [preview, setPreview] = useState<CsvImportPreview | null>(null);
  const fileInput = useRef<HTMLInputElement | null>(null);
  const returnTo = teamAccessReturnPath();
  const requestedTeamId = requestedDirectoryTeamId();
  const selectedTeam = teams.find((team) => team.id === selectedTeamId);

  async function selectTeam(team: TeamAccount) {
    setSelectedTeamId(team.id);
    setTeamDraft(team);
    setAdditionalLeadDraft(null);
    setMembers([]);
    setMemberDraft(null);
    setPreview(null);
    try {
      setMembers(await listDirectoryMembers(team.id));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to load the team roster.');
    }
  }

  async function loadAccess(): Promise<TeamDirectoryAccess | null> {
    const nextAccess = await getTeamDirectoryAccess();
    setAccess(nextAccess);
    if (!nextAccess) {
      setTeams([]);
      setSelectedTeamId('');
      setAdditionalLeadDraft(null);
      return null;
    }
    const nextTeams = hasAdministrativeAccess(nextAccess) ? await listDirectoryTeams() : nextAccess.team ? [nextAccess.team] : [];
    setTeams(nextTeams);
    const initialTeam = nextAccess.team || nextTeams.find((team) => team.id === requestedTeamId) || nextTeams[0];
    if (initialTeam) {
      await selectTeam(initialTeam);
    } else {
      setSelectedTeamId('');
      setTeamDraft(emptyTeamAccount());
    }
    return nextAccess;
  }

  useEffect(() => {
    let active = true;
    async function initialize() {
      try {
        const config = await getTeamAccessConfig();
        if (active) setTurnstileSiteKey(config.turnstileSiteKey);
        const token = new URLSearchParams(window.location.search).get('token');
        let verified = false;
        if (token) {
          try {
            await verifyTeamAccess(token);
            verified = true;
          } catch (nextError) {
            if (active) setError(nextError instanceof Error ? nextError.message : 'That access link could not be verified.');
          }
        }
        if ((verified || !token) && active) {
          const nextAccess = await loadAccess();
          if (nextAccess && returnTo) {
            window.location.replace(returnTo);
            return;
          }
        }
        window.history.replaceState({}, '', `${basePath}/team-directory`);
      } catch (nextError) {
        if (active) setError(nextError instanceof Error ? nextError.message : 'Unable to open the team directory.');
      } finally {
        if (active) setLoading(false);
      }
    }
    void initialize();
    return () => { active = false; };
  }, []);

  async function requestAccess(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      setMessage(await requestTeamAccess(accessEmail, turnstileToken, returnTo));
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to request an access link.');
    } finally {
      setBusy(false);
    }
  }

  async function saveTeam(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setBusy(true);
    setError('');
    try {
      const saved = selectedTeamId
        ? await updateDirectoryTeam(selectedTeamId, teamDraft)
        : await createDirectoryTeam(teamDraft);
      const nextTeams = teams.some((team) => team.id === saved.id)
        ? teams.map((team) => (team.id === saved.id ? saved : team))
        : [...teams, saved].sort((left, right) => `${left.school} ${left.teamName}`.localeCompare(`${right.school} ${right.teamName}`));
      setTeams(nextTeams);
      setMessage('Team account saved.');
      await selectTeam(saved);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to save the team account.');
    } finally {
      setBusy(false);
    }
  }

  async function verifyTeamInformation() {
    if (!selectedTeamId || !window.confirm('Verify that the Team and Team Leader information is current?')) return;
    setBusy(true);
    setError('');
    try {
      const saved = await verifyDirectoryTeam(selectedTeamId);
      updateSavedTeam(saved);
      setMessage('Team account information verified.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to verify the team account information.');
    } finally {
      setBusy(false);
    }
  }

  async function saveMember(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedTeamId || !memberDraft) return;
    setBusy(true);
    setError('');
    try {
      const assignedTeamId = memberDraft.assignedTeamId || selectedTeamId;
      const saved = memberDraft.id
        ? await updateDirectoryMember(selectedTeamId, memberDraft.id, memberDraft)
        : await createDirectoryMember(selectedTeamId, memberDraft);
      if (memberDraft.id && assignedTeamId !== selectedTeamId) {
        const assignedTeam = teams.find((team) => team.id === assignedTeamId);
        setMemberDraft(null);
        setMessage('Team member assigned to the selected school and team.');
        if (assignedTeam) await selectTeam(assignedTeam);
        return;
      }
      setMembers((current) => {
        const next = current.some((member) => member.id === saved.id)
          ? current.map((member) => (member.id === saved.id ? saved : member))
          : [...current, saved];
        return next.sort((left, right) => left.fullName.localeCompare(right.fullName));
      });
      setMemberDraft(null);
      setMessage('Team member saved.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to save this member.');
    } finally {
      setBusy(false);
    }
  }

  function updateSavedTeam(saved: TeamAccount) {
    setTeams((current) => current.some((team) => team.id === saved.id)
      ? current.map((team) => (team.id === saved.id ? saved : team))
      : [...current, saved].sort((left, right) => `${left.school} ${left.teamName}`.localeCompare(`${right.school} ${right.teamName}`)));
    setTeamDraft(saved);
  }

  async function saveAdditionalLead(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!selectedTeamId || !additionalLeadDraft) return;
    setBusy(true);
    setError('');
    try {
      const saved = additionalLeadDraft.id
        ? await updateDirectoryAdditionalLead(selectedTeamId, additionalLeadDraft.id, additionalLeadDraft)
        : await createDirectoryAdditionalLead(selectedTeamId, additionalLeadDraft);
      updateSavedTeam(saved);
      setAdditionalLeadDraft(null);
      setMessage('Additional team lead saved.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to save this additional team lead.');
    } finally {
      setBusy(false);
    }
  }

  async function removeAdditionalLead(lead: TeamAdditionalLead) {
    if (!selectedTeamId || !window.confirm(`Remove ${lead.fullName} as an additional team lead?`)) return;
    setBusy(true);
    setError('');
    try {
      const saved = await deleteDirectoryAdditionalLead(selectedTeamId, lead.id);
      updateSavedTeam(saved);
      if (additionalLeadDraft?.id === lead.id) setAdditionalLeadDraft(null);
      setMessage('Additional team lead removed and their access was revoked.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to remove this additional team lead.');
    } finally {
      setBusy(false);
    }
  }

  async function removeMember(member: TeamMember) {
    if (!selectedTeamId || !window.confirm(`Remove ${member.fullName} from this team?`)) return;
    setBusy(true);
    setError('');
    try {
      await deleteDirectoryMember(selectedTeamId, member.id);
      setMembers((current) => current.filter((item) => item.id !== member.id));
      if (memberDraft?.id === member.id) setMemberDraft(null);
      setMessage('Team member removed.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to remove this member.');
    } finally {
      setBusy(false);
    }
  }

  async function previewCsv(file: File) {
    if (!selectedTeamId) return;
    setBusy(true);
    setError('');
    try {
      setPreview(await previewDirectoryMemberImport(selectedTeamId, file));
      setMessage('CSV checked. Review the preview before applying valid rows.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to read that CSV file.');
    } finally {
      setBusy(false);
      if (fileInput.current) fileInput.current.value = '';
    }
  }

  async function applyCsvPreview() {
    if (!selectedTeamId || !preview) return;
    setBusy(true);
    setError('');
    try {
      const result = await applyDirectoryMemberImport(selectedTeamId, preview.id);
      setMembers(await listDirectoryMembers(selectedTeamId));
      setPreview(null);
      setMessage(`CSV applied: ${result.added} added, ${result.updated} updated.`);
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to apply the CSV preview.');
    } finally {
      setBusy(false);
    }
  }

  async function logout() {
    setBusy(true);
    try {
      await logoutTeamAccess();
      setAccess(null);
      setTeams([]);
      setAdditionalLeadDraft(null);
      setMembers([]);
      setSelectedTeamId('');
      setMessage('You have signed out.');
    } catch (nextError) {
      setError(nextError instanceof Error ? nextError.message : 'Unable to sign out.');
    } finally {
      setBusy(false);
    }
  }

  if (loading) {
    return <PageShell title="Team Directory" eyebrow="Private workspace"><Section title="Loading">Opening the private directory...</Section></PageShell>;
  }

  if (!access) {
    return (
      <PageShell title="Team Directory" eyebrow="Private workspace">
        <Section title="Email access link">
          <form className="accessForm" onSubmit={requestAccess}>
            <Field label="Team lead or administrator email" required>
              <input type="email" value={accessEmail} onChange={(event) => setAccessEmail(event.target.value)} required autoComplete="email" />
            </Field>
            {turnstileSiteKey ? <TurnstileWidget siteKey={turnstileSiteKey} onToken={setTurnstileToken} /> : null}
            <button className="primaryButton" type="submit" disabled={busy || Boolean(turnstileSiteKey && !turnstileToken)}>
              {busy ? 'Sending...' : 'Email access link'}
            </button>
          </form>
          {message ? <p className="status good"><CheckCircle2 size={18} /> {message}</p> : null}
          {error ? <p className="status bad" role="alert"><AlertTriangle size={18} /> {error}</p> : null}
        </Section>
      </PageShell>
    );
  }

  if (access.role === 'lco') {
    return (
      <PageShell title="Launch Operations" eyebrow="LCO workspace">
        <Section title="Flight Cards">
          <div className="actionBar">
            <button className="primaryButton" type="button" onClick={() => navigate('launch-operations')}><ClipboardList size={18} /> Open Launch Operations</button>
            <button type="button" onClick={() => void logout()} disabled={busy}><LogOut size={17} /> Sign out</button>
          </div>
        </Section>
      </PageShell>
    );
  }

  return (
    <PageShell title="Team Directory" eyebrow={access.role === 'board' ? 'URRG board member' : hasAdministrativeAccess(access) ? 'URRG administrator' : 'Team lead'}>
      <section className="directoryToolbar">
        {hasAdministrativeAccess(access) ? (
          <label className="directoryTeamPicker">
            <span>Team</span>
            <select
              value={selectedTeamId}
              onChange={(event) => {
                const nextId = event.target.value;
                if (!nextId) {
                  setSelectedTeamId('');
                  setTeamDraft(emptyTeamAccount());
                  setAdditionalLeadDraft(null);
                  setMembers([]);
                  setMemberDraft(null);
                  setPreview(null);
                  return;
                }
                const team = teams.find((item) => item.id === nextId);
                if (team) void selectTeam(team);
              }}
            >
              <option value="">New team account</option>
              {teams.map((team) => <option key={team.id} value={team.id}>{team.school} - {team.teamName}</option>)}
            </select>
          </label>
        ) : <p className="directoryIdentity">{access.team?.school} - {access.team?.teamName}</p>}
        <div className="directoryToolbarActions">
          {hasAdministrativeAccess(access) ? <button type="button" onClick={() => navigate('team-summary')}><BarChart3 size={17} /> School summary</button> : null}
          {hasAdministrativeAccess(access) ? <button type="button" onClick={() => navigate('admins')}><ShieldCheck size={17} /> Administrators</button> : null}
          <button type="button" onClick={() => void logout()} disabled={busy} title="Sign out of Team Directory"><LogOut size={17} /> Sign out</button>
        </div>
      </section>
      {message ? <p className="directoryMessage status good"><CheckCircle2 size={18} /> {message}</p> : null}
      {error ? <p className="directoryMessage status bad" role="alert"><AlertTriangle size={18} /> {error}</p> : null}
      <form onSubmit={saveTeam}>
        <Section title="Team">
          <div className="grid three">
            <Field label="Team name" required><input value={teamDraft.teamName} onChange={(event) => setTeamDraft({ ...teamDraft, teamName: event.target.value })} required /></Field>
            <Field label="School" required><input value={teamDraft.school} onChange={(event) => setTeamDraft({ ...teamDraft, school: event.target.value })} required /></Field>
            <Field label="Team email account"><input type="email" value={teamDraft.teamEmail} onChange={(event) => setTeamDraft({ ...teamDraft, teamEmail: event.target.value })} /></Field>
            <Field label="Team Discord channel"><input value={teamDraft.teamDiscordChannel} onChange={(event) => setTeamDraft({ ...teamDraft, teamDiscordChannel: event.target.value })} /></Field>
            <Field label="ClubExpress memberNumber"><input value={teamDraft.clubexpressMemberNumber} disabled={!hasAdministrativeAccess(access)} onChange={(event) => setTeamDraft({ ...teamDraft, clubexpressMemberNumber: event.target.value })} /></Field>
            <Field label="CRP membership status"><select value={teamDraft.crpMembershipStatus} disabled={!hasAdministrativeAccess(access)} onChange={(event) => setTeamDraft({ ...teamDraft, crpMembershipStatus: event.target.value })}><option>Active</option><option>Inactive</option><option>Expired</option>{teamDraft.crpMembershipStatus === 'System' ? <option>System</option> : null}</select></Field>
            <Field label="Membership start"><input type="date" value={teamDraft.membershipStart} disabled={!hasAdministrativeAccess(access)} onChange={(event) => setTeamDraft({ ...teamDraft, membershipStart: event.target.value })} /></Field>
            <Field label="Membership end"><input type="date" value={teamDraft.membershipEnd} disabled={!hasAdministrativeAccess(access)} onChange={(event) => setTeamDraft({ ...teamDraft, membershipEnd: event.target.value })} /></Field>
            <Field label="Faculty advisor"><input value={teamDraft.facultyAdvisor} onChange={(event) => setTeamDraft({ ...teamDraft, facultyAdvisor: event.target.value })} /></Field>
            <Field label="School billing contact"><input value={teamDraft.schoolBillingContact} onChange={(event) => setTeamDraft({ ...teamDraft, schoolBillingContact: event.target.value })} /></Field>
            {selectedTeam ? <Field label="Last updated"><input value={formatDirectoryTimestamp(selectedTeam.updatedAt)} disabled readOnly /></Field> : null}
            {selectedTeam ? <Field label="Team lead verification"><input value={selectedTeam.teamLeadVerifiedAt ? formatDirectoryTimestamp(selectedTeam.teamLeadVerifiedAt) : 'Not verified'} disabled readOnly /></Field> : null}
            {selectedTeam ? <Field label="Verified by"><input value={selectedTeam.teamLeadVerifiedByEmail || 'Not recorded'} disabled readOnly /></Field> : null}
          </div>
          <Field label="Notes"><textarea value={teamDraft.notes} onChange={(event) => setTeamDraft({ ...teamDraft, notes: event.target.value })} /></Field>
        </Section>
        <Section title="Team Leader">
          <div className="grid three">
            <Field label="Primary team lead name"><input value={teamDraft.primaryTeamLeadName} onChange={(event) => setTeamDraft({ ...teamDraft, primaryTeamLeadName: event.target.value })} /></Field>
            <Field label="Primary team lead email"><input type="email" value={teamDraft.primaryTeamLeadEmail} onChange={(event) => setTeamDraft({ ...teamDraft, primaryTeamLeadEmail: event.target.value })} /></Field>
            <Field label="Primary team lead phone"><input type="tel" value={teamDraft.primaryTeamLeadPhone} onChange={(event) => setTeamDraft({ ...teamDraft, primaryTeamLeadPhone: event.target.value })} /></Field>
            <Field label="Primary lead Discord username"><input value={teamDraft.primaryTeamLeadDiscordUsername} onChange={(event) => setTeamDraft({ ...teamDraft, primaryTeamLeadDiscordUsername: event.target.value })} /></Field>
            <Field label="Primary lead expiration"><input type="date" value={teamDraft.primaryTeamLeadExpiration} disabled={!hasAdministrativeAccess(access)} onChange={(event) => setTeamDraft({ ...teamDraft, primaryTeamLeadExpiration: event.target.value })} /></Field>
            <Field label="Graduation year"><input type="number" min="1900" max="2100" value={teamDraft.graduationYear ?? ''} onChange={(event) => setTeamDraft({ ...teamDraft, graduationYear: numericYear(event.target.value) })} /></Field>
          </div>
        </Section>
        <div className="actionBar"><button className="primaryButton" type="submit" disabled={busy}><Save size={18} /> {selectedTeamId ? 'Save team account' : 'Create team account'}</button>{access.role === 'lead' && selectedTeam && !selectedTeam.isSystem ? <button type="button" onClick={() => void verifyTeamInformation()} disabled={busy}><CheckCircle2 size={18} /> Verify team information</button> : null}</div>
      </form>
      {selectedTeamId && selectedTeam && !selectedTeam.isSystem ? (
        <Section title={`Additional Team Leads (${selectedTeam.additionalLeads.length})`}>
          {hasAdministrativeAccess(access) ? <div className="directoryActions"><button type="button" onClick={() => setAdditionalLeadDraft(emptyAdditionalTeamLead())} disabled={busy}><UserPlus size={17} /> Add additional lead</button></div> : null}
          <div className="tableWrap">
            <table className="memberTable"><thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Discord</th><th>Expires</th>{hasAdministrativeAccess(access) ? <th></th> : null}</tr></thead>
              <tbody>{selectedTeam.additionalLeads.map((lead) => <tr key={lead.id}><td>{lead.fullName}</td><td>{lead.email}</td><td>{lead.phone}</td><td>{lead.discordUsername}</td><td>{lead.expirationDate}</td>{hasAdministrativeAccess(access) ? <td className="rowActions"><button type="button" onClick={() => setAdditionalLeadDraft({ ...lead })} disabled={busy} title={`Edit ${lead.fullName}`}><FileText size={16} /></button><button className="iconButton danger" type="button" onClick={() => void removeAdditionalLead(lead)} disabled={busy} title={`Remove ${lead.fullName} as an additional team lead`}><Trash2 size={16} /></button></td> : null}</tr>)}</tbody>
            </table>
          </div>
          {hasAdministrativeAccess(access) && additionalLeadDraft ? (
            <form className="memberEditor" onSubmit={saveAdditionalLead}>
              <div className="repeaterHeader"><h3>{additionalLeadDraft.id ? 'Edit additional team lead' : 'Add additional team lead'}</h3><button type="button" onClick={() => setAdditionalLeadDraft(null)} title="Close additional team lead editor"><X size={17} /></button></div>
              <div className="grid three">
                <Field label="Full name" required><input value={additionalLeadDraft.fullName} onChange={(event) => setAdditionalLeadDraft({ ...additionalLeadDraft, fullName: event.target.value })} required /></Field>
                <Field label="Email" required><input type="email" value={additionalLeadDraft.email} onChange={(event) => setAdditionalLeadDraft({ ...additionalLeadDraft, email: event.target.value })} required /></Field>
                <Field label="Phone"><input type="tel" value={additionalLeadDraft.phone} onChange={(event) => setAdditionalLeadDraft({ ...additionalLeadDraft, phone: event.target.value })} /></Field>
                <Field label="Discord username"><input value={additionalLeadDraft.discordUsername} onChange={(event) => setAdditionalLeadDraft({ ...additionalLeadDraft, discordUsername: event.target.value })} /></Field>
                <Field label="Lead expiration"><input type="date" value={additionalLeadDraft.expirationDate} onChange={(event) => setAdditionalLeadDraft({ ...additionalLeadDraft, expirationDate: event.target.value })} /></Field>
              </div>
              <div className="actionBar"><button className="primaryButton" type="submit" disabled={busy}><Save size={18} /> Save additional team lead</button></div>
            </form>
          ) : null}
        </Section>
      ) : null}
      {selectedTeamId ? (
        <>
          <Section title={`Team Members (${members.length})`}>
            <div className="directoryActions">
              <button type="button" onClick={() => setMemberDraft(emptyTeamMember(hasAdministrativeAccess(access) ? 'Active' : 'Needs review'))} disabled={busy}><UserPlus size={17} /> Add member</button>
              <button type="button" onClick={() => fileInput.current?.click()} disabled={busy}><Upload size={17} /> Upload CSV</button>
              <button type="button" onClick={() => { const link = document.createElement('a'); link.href = `/urrg/api/team-directory/teams/${selectedTeamId}/members-template.csv`; link.click(); }} disabled={busy}><Download size={17} /> Download template</button>
              <button type="button" onClick={() => { const link = document.createElement('a'); link.href = `/urrg/api/team-directory/teams/${selectedTeamId}/members.csv`; link.click(); }} disabled={busy}><Download size={17} /> Download CSV</button>
              <input ref={fileInput} className="fileInput" type="file" accept=".csv,text/csv" onChange={(event) => { const file = event.target.files?.[0]; if (file) void previewCsv(file); }} />
            </div>
            {preview ? (
              <div className="importPreview">
                <strong>CSV preview</strong>
                <span>{preview.validRows} valid rows: {preview.newMembers} new, {preview.updatedMembers} updates.</span>
                {preview.errors.length ? <span>{preview.errors.length} rows need correction before they can be included.</span> : null}
                {preview.errors.length ? <div className="tableWrap"><table><thead><tr><th>Row</th><th>Issue</th></tr></thead><tbody>{preview.errors.map((item) => <tr key={`${item.rowNumber}-${item.message}`}><td>{item.rowNumber}</td><td>{item.message}</td></tr>)}</tbody></table></div> : null}
                <button className="primaryButton" type="button" disabled={busy || preview.validRows === 0} onClick={() => void applyCsvPreview()}><Upload size={17} /> Apply valid rows</button>
              </div>
            ) : null}
            <div className="tableWrap">
              <table className="memberTable"><thead><tr><th>Name</th><th>Email</th><th>Phone</th><th>Discord</th><th>Status</th><th>Cert</th><th>NAR / TRA</th><th>Member#</th><th></th></tr></thead>
                <tbody>{members.map((member) => <tr key={member.id}><td>{member.fullName}</td><td>{member.email}</td><td>{member.phone}</td><td>{member.discordUsername}</td><td>{member.membershipStatus}</td><td>{member.certificationLevel}</td><td>{[member.narNumber && `NAR ${member.narNumber}`, member.traNumber && `TRA ${member.traNumber}`].filter(Boolean).join(' | ')}</td><td>{member.memberNumber}</td><td className="rowActions"><button type="button" onClick={() => setMemberDraft({ ...member, assignedTeamId: selectedTeam?.isSystem ? '' : selectedTeamId })} disabled={busy} title={`Edit ${member.fullName}`}><FileText size={16} /></button><button className="iconButton danger" type="button" onClick={() => void removeMember(member)} disabled={busy} title={`Remove ${member.fullName}`}><Trash2 size={16} /></button></td></tr>)}</tbody>
              </table>
            </div>
            {memberDraft ? (
              <form className="memberEditor" onSubmit={saveMember}>
                <div className="repeaterHeader"><h3>{memberDraft.id ? 'Edit member' : 'Add member'}</h3><button type="button" onClick={() => setMemberDraft(null)} title="Close member editor"><X size={17} /></button></div>
                <div className="grid three">
                  <Field label="Full name" required><input value={memberDraft.fullName} onChange={(event) => setMemberDraft({ ...memberDraft, fullName: event.target.value })} required /></Field>
                  <Field label="Email"><input type="email" value={memberDraft.email} onChange={(event) => setMemberDraft({ ...memberDraft, email: event.target.value })} /></Field>
                  <Field label="Phone"><input type="tel" value={memberDraft.phone} onChange={(event) => setMemberDraft({ ...memberDraft, phone: event.target.value })} /></Field>
                  <Field label="Discord username"><input value={memberDraft.discordUsername} onChange={(event) => setMemberDraft({ ...memberDraft, discordUsername: event.target.value })} /></Field>
                  <Field label="Street address"><input value={memberDraft.streetAddress} onChange={(event) => setMemberDraft({ ...memberDraft, streetAddress: event.target.value })} /></Field>
                  <Field label="City"><input value={memberDraft.city} onChange={(event) => setMemberDraft({ ...memberDraft, city: event.target.value })} /></Field>
                  <Field label="State"><input value={memberDraft.state} onChange={(event) => setMemberDraft({ ...memberDraft, state: event.target.value })} /></Field>
                  <Field label="ZIP / Postal code"><input value={memberDraft.postalCode} onChange={(event) => setMemberDraft({ ...memberDraft, postalCode: event.target.value })} /></Field>
                  <Field label="Country"><input value={memberDraft.country} onChange={(event) => setMemberDraft({ ...memberDraft, country: event.target.value })} /></Field>
                  <Field label="Graduation year"><input type="number" min="1900" max="2100" value={memberDraft.graduationYear ?? ''} onChange={(event) => setMemberDraft({ ...memberDraft, graduationYear: numericYear(event.target.value) })} /></Field>
                  <Field label="Certification level"><select value={memberDraft.certificationLevel} onChange={(event) => setMemberDraft({ ...memberDraft, certificationLevel: event.target.value })}><option>None</option><option>TMP/JrHPP</option><option>L1</option><option>L2</option><option>L3</option></select></Field>
                  <Field label="NAR number"><input value={memberDraft.narNumber} onChange={(event) => setMemberDraft({ ...memberDraft, narNumber: event.target.value })} /></Field>
                  <Field label="NAR expiration"><input type="date" value={memberDraft.narExpiration} onChange={(event) => setMemberDraft({ ...memberDraft, narExpiration: event.target.value })} /></Field>
                  <Field label="TRA number"><input value={memberDraft.traNumber} onChange={(event) => setMemberDraft({ ...memberDraft, traNumber: event.target.value })} /></Field>
                  <Field label="TRA expiration"><input type="date" value={memberDraft.traExpiration} onChange={(event) => setMemberDraft({ ...memberDraft, traExpiration: event.target.value })} /></Field>
                  <Field label="L2 exam passed?"><select value={memberDraft.l2ExamPassed === null ? '' : memberDraft.l2ExamPassed ? 'Yes' : 'No'} onChange={(event) => { const value = event.target.value; setMemberDraft({ ...memberDraft, l2ExamPassed: value === '' ? null : value === 'Yes', l2ExamDate: value === 'Yes' ? memberDraft.l2ExamDate : '' }); }}><option value="">Not recorded</option><option>Yes</option><option>No</option></select></Field>
                  <Field label="L2 exam date"><input type="date" value={memberDraft.l2ExamDate} disabled={memberDraft.l2ExamPassed !== true} onChange={(event) => setMemberDraft({ ...memberDraft, l2ExamDate: event.target.value })} /></Field>
                  <Field label="URRG team member#"><input value={memberDraft.memberNumber} disabled onChange={(event) => setMemberDraft({ ...memberDraft, memberNumber: event.target.value })} /></Field>
                  <Field label="Membership status"><select value={memberDraft.membershipStatus} disabled={!hasAdministrativeAccess(access)} onChange={(event) => setMemberDraft({ ...memberDraft, membershipStatus: event.target.value as TeamMemberInput['membershipStatus'] })}><option>Active</option><option>Inactive</option><option>Graduated</option><option>Needs review</option></select></Field>
                  <Field label="URRG membership expiration"><input type="date" value={memberDraft.membershipExpiration} disabled onChange={(event) => setMemberDraft({ ...memberDraft, membershipExpiration: event.target.value })} /></Field>
                  {hasAdministrativeAccess(access) && memberDraft.id ? <Field label="School / team"><select value={memberDraft.assignedTeamId || ''} onChange={(event) => setMemberDraft({ ...memberDraft, assignedTeamId: event.target.value })}>{selectedTeam?.isSystem ? <option value="">Needs team assignment</option> : null}{teams.filter((team) => !team.isSystem).map((team) => <option key={team.id} value={team.id}>{team.school} - {team.teamName}</option>)}</select></Field> : null}
                </div>
                <Field label="Notes"><textarea value={memberDraft.notes} onChange={(event) => setMemberDraft({ ...memberDraft, notes: event.target.value })} /></Field>
                <div className="actionBar"><button className="primaryButton" type="submit" disabled={busy}><Save size={18} /> Save member</button></div>
              </form>
            ) : null}
          </Section>
        </>
      ) : null}
    </PageShell>
  );
}

function ManifestPage() {
  const { result, isSubmitting, submit: submitForm } = useSubmission();
  const launchSelection = useLaunchEventSelection();
  const [flyers, setFlyers] = useState<ManifestFlyer[]>([blankFlyer()]);
  const [nonFlyers, setNonFlyers] = useState<ManifestPerson[]>([blankPerson()]);
  const [directoryAccess, setDirectoryAccess] = useState<TeamDirectoryAccess | null>(null);
  const [directoryTeams, setDirectoryTeams] = useState<TeamAccount[]>([]);
  const [selectedDirectoryTeam, setSelectedDirectoryTeam] = useState<TeamAccount | null>(null);
  const [directoryMembers, setDirectoryMembers] = useState<TeamMember[]>([]);
  const [rosterRoles, setRosterRoles] = useState<Record<string, ManifestRosterRole>>({});
  const [directoryLoading, setDirectoryLoading] = useState(false);
  const [directoryError, setDirectoryError] = useState('');
  const [directorySyncMessage, setDirectorySyncMessage] = useState('');
  const [directorySyncError, setDirectorySyncError] = useState('');
  const [directorySyncing, setDirectorySyncing] = useState(false);
  const [directoryLoggingOut, setDirectoryLoggingOut] = useState(false);
  const rosterRequest = useRef(0);
  const [form, setForm] = useState({
    submitterName: '',
    submitterEmail: '',
    schoolName: '',
    teamName: '',
    dateStart: '',
    dateEnd: '',
    rangeRulesAgreed: false,
  });

  const activeDirectoryMembers = useMemo(
    () => directoryMembers.filter((member) => member.membershipStatus === 'Active'),
    [directoryMembers],
  );

  useEffect(() => {
    void loadDirectoryContext();
  }, []);

  useEffect(() => {
    const selectedEvent = launchSelection.events.find((event) => event.id === launchSelection.selectedEventId);
    if (!selectedEvent) return;
    setForm((current) => ({
      ...current,
      dateStart: selectedEvent.startDate,
      dateEnd: selectedEvent.endDate,
    }));
  }, [launchSelection.events, launchSelection.selectedEventId]);

  async function loadDirectoryContext() {
    try {
      const access = await getTeamDirectoryAccess();
      setDirectoryAccess(access);
      if (!access) return;
      const teams = hasAdministrativeAccess(access) ? await listDirectoryTeams() : access.team ? [access.team] : [];
      setDirectoryTeams(teams);
      if (access.role === 'lead' && access.team) {
        await loadDirectoryTeam(access.team);
      }
    } catch {
      setDirectoryError('Unable to load the team roster.');
    }
  }

  async function loadDirectoryTeam(team: TeamAccount) {
    const requestId = rosterRequest.current + 1;
    rosterRequest.current = requestId;
    setSelectedDirectoryTeam(team);
    setDirectoryLoading(true);
    setDirectoryError('');
    setDirectoryMembers([]);
    setRosterRoles({});
    setForm((current) => ({
      ...current,
      submitterName: team.primaryTeamLeadName,
      submitterEmail: team.primaryTeamLeadEmail,
      schoolName: team.school,
      teamName: team.teamName,
    }));
    try {
      const members = await listDirectoryMembers(team.id);
      if (rosterRequest.current !== requestId) return;
      setDirectoryMembers(members);
      setRosterRoles(Object.fromEntries(members.map((member) => [member.id, 'none' as ManifestRosterRole])));
    } catch {
      if (rosterRequest.current === requestId) setDirectoryError('Unable to load the team roster.');
    } finally {
      if (rosterRequest.current === requestId) setDirectoryLoading(false);
    }
  }

  function addRosterSelections() {
    const selectedFlyers = activeDirectoryMembers
      .filter((member) => rosterRoles[member.id] === 'flyer')
      .map(manifestFlyerFromMember);
    const selectedNonFlyers = activeDirectoryMembers
      .filter((member) => rosterRoles[member.id] === 'nonFlyer')
      .map(manifestPersonFromMember);
    if (selectedFlyers.length) setFlyers((current) => appendManifestRosterRows(current, selectedFlyers));
    if (selectedNonFlyers.length) setNonFlyers((current) => appendManifestRosterRows(current, selectedNonFlyers));
  }

  async function logoutDirectory() {
    setDirectoryLoggingOut(true);
    setDirectoryError('');
    try {
      await logoutTeamAccess();
      window.location.replace(`${basePath}/manifest`);
    } catch (error) {
      setDirectoryError(error instanceof Error ? error.message : 'Unable to sign out.');
      setDirectoryLoggingOut(false);
    }
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    setDirectorySyncMessage('');
    setDirectorySyncError('');
    const submission = await submitForm('manifest', { ...form, flyers, nonFlyers }, getHoneypot(formElement), launchSelection.selectedEventId);
    if (submission?.mode !== 'server' || !directoryAccess || !selectedDirectoryTeam) return;

    const directoryFlyers = manifestDirectoryPeople(flyers);
    const directoryNonFlyers = manifestDirectoryPeople(nonFlyers);
    if (!directoryFlyers.length && !directoryNonFlyers.length) return;

    setDirectorySyncing(true);
    try {
      const synced = await syncManifestDirectoryMembers(selectedDirectoryTeam.id, {
        referenceId: submission.referenceId,
        flyers: directoryFlyers,
        nonFlyers: directoryNonFlyers,
      });
      const parts = [];
      if (synced.added) parts.push(`${synced.added} added`);
      if (synced.alreadyPresent) parts.push(`${synced.alreadyPresent} already in the directory`);
      setDirectorySyncMessage(parts.length ? `Team Directory: ${parts.join('; ')}.` : 'Team Directory: no new people to add.');
    } catch (error) {
      setDirectorySyncError(error instanceof Error ? `The manifest was received, but the Team Directory could not be updated: ${error.message}` : 'The manifest was received, but the Team Directory could not be updated.');
    } finally {
      setDirectorySyncing(false);
    }
  }

  return (
    <PageShell title="Team Manifest" eyebrow="Public form">
      <form onSubmit={submit}>
        <Honeypot />
        <LaunchWeekendSelector selection={launchSelection} />
        {directoryAccess ? (
          <Section title="Team Roster">
            <div className="manifestRosterToolbar">
              <p className="directoryIdentity">
                {hasAdministrativeAccess(directoryAccess)
                  ? `${directoryAccess.role === 'board' ? 'URRG board member' : 'URRG administrator'}: ${directoryAccess.email}`
                  : `${selectedDirectoryTeam?.school} - ${selectedDirectoryTeam?.teamName}`}
              </p>
              {hasAdministrativeAccess(directoryAccess) ? (
                <Field label="Team" required>
                  <select
                    required
                    value={selectedDirectoryTeam?.id || ''}
                    onChange={(event) => {
                      const team = directoryTeams.find((item) => item.id === event.target.value);
                      if (team) void loadDirectoryTeam(team);
                    }}
                  >
                    <option value="">Select team</option>
                    {directoryTeams.filter((team) => !team.isSystem).map((team) => <option key={team.id} value={team.id}>{team.school} - {team.teamName}</option>)}
                  </select>
                </Field>
              ) : null}
              <button type="button" onClick={() => void logoutDirectory()} disabled={directoryLoggingOut} title="Sign out of team roster"><LogOut size={17} /> Sign out</button>
            </div>
            {directoryError ? <p className="directoryMessage status bad" role="alert"><AlertTriangle size={18} /> {directoryError}</p> : null}
            {directoryLoading ? <p className="directoryMessage">Loading roster...</p> : null}
            {!directoryLoading && selectedDirectoryTeam && activeDirectoryMembers.length === 0 ? <p className="directoryMessage">No active team members are available.</p> : null}
            {activeDirectoryMembers.length ? (
              <>
                <div className="tableWrap">
                  <table className="memberTable">
                    <thead><tr><th>Name</th><th>Email</th><th>Certification</th><th>Manifest role</th></tr></thead>
                    <tbody>{activeDirectoryMembers.map((member) => <tr key={member.id}>
                      <td>{member.fullName}</td><td>{member.email}</td><td>{member.certificationLevel}</td>
                      <td><select value={rosterRoles[member.id] || 'none'} onChange={(event) => setRosterRoles({ ...rosterRoles, [member.id]: event.target.value as ManifestRosterRole })}><option value="none">Not included</option><option value="flyer">Flyer</option><option value="nonFlyer">Non-flyer</option></select></td>
                    </tr>)}</tbody>
                  </table>
                </div>
                <div className="manifestRosterActions"><button type="button" onClick={addRosterSelections}><Users size={17} /> Add selected people</button></div>
              </>
            ) : null}
          </Section>
        ) : (
          <Section title="Team Roster">
            <div className="manifestRosterActions">
              <button type="button" onClick={() => navigateToTeamDirectory(`${basePath}/manifest`)}><Users size={17} /> Sign in as team lead or URRG administrator</button>
            </div>
          </Section>
        )}
        <Section title="Submitter">
          <div className="grid three">
            <Field label="Name" required>
              <input value={form.submitterName} onChange={(event) => setForm({ ...form, submitterName: event.target.value })} required />
            </Field>
            <Field label="Email" required help="Used by CRP to follow up about this manifest.">
              <input type="email" value={form.submitterEmail} onChange={(event) => setForm({ ...form, submitterEmail: event.target.value })} required />
            </Field>
            {!directoryAccess ? (
              <>
                <Field label="School">
                  <input value={form.schoolName} onChange={(event) => setForm({ ...form, schoolName: event.target.value })} />
                </Field>
                <Field label="Team">
                  <input value={form.teamName} onChange={(event) => setForm({ ...form, teamName: event.target.value })} />
                </Field>
              </>
            ) : null}
          </div>
          <label className="checkSingle">
            <input
              type="checkbox"
              checked={form.rangeRulesAgreed}
              onChange={(event) => setForm({ ...form, rangeRulesAgreed: event.target.checked })}
            />
            I have read and agree to the URRG range rules.
          </label>
        </Section>
        <ManifestTable title="Flyers" rows={flyers} setRows={setFlyers} kind="flyer" />
        <ManifestTable title="Non-Flyers" rows={nonFlyers} setRows={setNonFlyers} kind="person" />
        <ActionBar result={result} isSubmitting={isSubmitting || directorySyncing}>
          {directorySyncMessage ? <p className="status good"><CheckCircle2 size={18} /> {directorySyncMessage}</p> : null}
          {directorySyncError ? <p className="status bad" role="alert"><AlertTriangle size={18} /> {directorySyncError}</p> : null}
        </ActionBar>
      </form>
    </PageShell>
  );
}

function ManifestTable<T extends ManifestPerson>({
  title,
  rows,
  setRows,
  kind,
}: {
  title: string;
  rows: T[];
  setRows: (rows: T[]) => void;
  kind: 'flyer' | 'person';
}) {
  function patch(id: string, patchValues: Partial<T>) {
    setRows(rows.map((row) => (row.id === id ? { ...row, ...patchValues } : row)));
  }

  function remove(id: string) {
    if (rows.length === 1) return;
    setRows(rows.filter((row) => row.id !== id));
  }

  function add() {
    setRows([...(rows as T[]), (kind === 'flyer' ? blankFlyer() : blankPerson()) as T]);
  }

  return (
    <Section title={title}>
      <div className="tableWrap">
        <table>
          <thead>
            <tr>
              <th>Day</th>
              {kind === 'flyer' ? <th>Group</th> : null}
              <th>Name</th>
              <th>Email</th>
              {kind === 'flyer' ? <th>Flight type</th> : null}
              {kind === 'flyer' ? <th>Rocket</th> : null}
              {kind === 'flyer' ? <th>Colors</th> : null}
              <th>NAR/TRA</th>
              <th>Exp date</th>
              <th>Notes</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const flyer = row as unknown as ManifestFlyer;
              return (
                <tr key={row.id}>
                  <td><input type="date" value={row.day} onChange={(event) => patch(row.id, { day: event.target.value } as Partial<T>)} /></td>
                  {kind === 'flyer' ? <td><input value={flyer.group} onChange={(event) => patch(row.id, { group: event.target.value } as unknown as Partial<T>)} /></td> : null}
                  <td><input value={row.name} onChange={(event) => patch(row.id, { name: event.target.value } as Partial<T>)} /></td>
                  <td><input type="email" value={row.email} onChange={(event) => patch(row.id, { email: event.target.value } as Partial<T>)} /></td>
                  {kind === 'flyer' ? (
                    <td>
                      <select value={flyer.flightType} onChange={(event) => patch(row.id, { flightType: event.target.value } as unknown as Partial<T>)}>
                        <option>Standard</option>
                        <option>L1 Cert</option>
                        <option>L2 Cert</option>
                        <option>Team Project</option>
                      </select>
                    </td>
                  ) : null}
                  {kind === 'flyer' ? <td><input value={flyer.rocketName} onChange={(event) => patch(row.id, { rocketName: event.target.value } as unknown as Partial<T>)} /></td> : null}
                  {kind === 'flyer' ? <td><input value={flyer.rocketColors} onChange={(event) => patch(row.id, { rocketColors: event.target.value } as unknown as Partial<T>)} /></td> : null}
                  <td><input value={row.narTra} onChange={(event) => patch(row.id, { narTra: event.target.value } as Partial<T>)} /></td>
                  <td><input type="date" value={row.expDate} onChange={(event) => patch(row.id, { expDate: event.target.value } as Partial<T>)} /></td>
                  <td><input value={row.notes} onChange={(event) => patch(row.id, { notes: event.target.value } as Partial<T>)} /></td>
                  <td>
                    <button className="iconButton danger" type="button" onClick={() => remove(row.id)} title="Remove row">
                      <Trash2 size={17} />
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <button type="button" onClick={add}>
        <Plus size={17} /> Add {kind === 'flyer' ? 'flyer' : 'non-flyer'}
      </button>
    </Section>
  );
}

function FlightCardPage() {
  const { result, isSubmitting, submit: submitForm } = useSubmission();
  const launchSelection = useLaunchEventSelection();
  const [motors, setMotors] = useState<MotorRow[]>([blankMotor()]);
  const [altitude, setAltitude] = useState('10000');
  const [launchGuide, setLaunchGuide] = useState('');
  const [flightType, setFlightType] = useState('Standard');
  const [towerNeeded, setTowerNeeded] = useState('No');
  const [towerSource, setTowerSource] = useState('Own tower');
  const [recoveryMethod, setRecoveryMethod] = useState('');
  const [recoveryOther, setRecoveryOther] = useState('');
  const [redundantDeployment, setRedundantDeployment] = useState('No');
  const [redundancyMethod, setRedundancyMethod] = useState('');
  const [flightComputer, setFlightComputer] = useState('');
  const [mainDeployAltitude, setMainDeployAltitude] = useState('');
  const [tracking, setTracking] = useState('No');
  const [trackingType, setTrackingType] = useState('');
  const [specialInfo, setSpecialInfo] = useState('');
  const [notes, setNotes] = useState('');
  const [certFlight, setCertFlight] = useState('No');
  const [certAttempt, setCertAttempt] = useState('');
  const [membershipOrgs, setMembershipOrgs] = useState<string[]>([]);
  const [profile, setProfile] = useState({
    flyerName: '',
    hometown: '',
    flyerEmail: '',
    flyerPhone: '',
    currentCertLevel: '',
    rocketName: '',
    rocketColor: '',
    weightLb: '',
    lengthIn: '',
    rocketDiameterIn: '',
    tripoliNumber: '',
    tripoliExpiration: '',
    narNumber: '',
    narExpiration: '',
    urrgMember: '',
  });

  function toggleMembership(org: string) {
    setMembershipOrgs(
      membershipOrgs.includes(org)
        ? membershipOrgs.filter((item) => item !== org)
        : [...membershipOrgs, org],
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    await submitForm('flight-card', {
      ...profile,
      certFlight,
      certAttempt,
      membershipOrgs,
      altitude,
      launchGuide,
      flightType,
      towerNeeded,
      towerSource: towerNeeded === 'Yes' ? towerSource : '',
      recoveryMethod,
      recoveryOther: recoveryMethod === 'Other' ? recoveryOther : '',
      redundantDeployment,
      redundancyMethod: redundantDeployment === 'Yes' ? redundancyMethod : '',
      flightComputer: recoveryMethod === 'Dual deployment' ? flightComputer : '',
      mainDeployAltitude: recoveryMethod === 'Dual deployment' ? mainDeployAltitude : '',
      tracking,
      trackingType: tracking === 'Yes' ? trackingType : '',
      specialInfo,
      notes,
      motors,
    }, getHoneypot(formElement), launchSelection.selectedEventId);
  }

  return (
    <PageShell title="Flight Card" eyebrow="Standalone">
      <form onSubmit={submit}>
        <Honeypot />
        <LaunchWeekendSelector selection={launchSelection} />
        <Section title="Flyer and Rocket">
          <div className="grid four">
            <Field label="Flyer name" required>
              <input value={profile.flyerName} onChange={(event) => setProfile({ ...profile, flyerName: event.target.value })} required />
            </Field>
            <Field label="Hometown">
              <input value={profile.hometown} onChange={(event) => setProfile({ ...profile, hometown: event.target.value })} />
            </Field>
            <Field label="Flyer email" required help="Used by URRG to follow up about this flight.">
              <input type="email" value={profile.flyerEmail} onChange={(event) => setProfile({ ...profile, flyerEmail: event.target.value })} required />
            </Field>
            <Field label="Phone">
              <input value={profile.flyerPhone} onChange={(event) => setProfile({ ...profile, flyerPhone: event.target.value })} />
            </Field>
            <Field label="Current cert level">
              <select value={profile.currentCertLevel} onChange={(event) => setProfile({ ...profile, currentCertLevel: event.target.value })}>
                <option value=""></option>
                <option>None</option>
                <option>TMP/JrHPP</option>
                <option>L1</option>
                <option>L2</option>
                <option>L3</option>
              </select>
            </Field>
            <Field label="Rocket name">
              <input value={profile.rocketName} onChange={(event) => setProfile({ ...profile, rocketName: event.target.value })} />
            </Field>
            <Field label="Rocket color">
              <input value={profile.rocketColor} onChange={(event) => setProfile({ ...profile, rocketColor: event.target.value })} />
            </Field>
            <Field label="Weight">
              <input placeholder="lb" value={profile.weightLb} onChange={(event) => setProfile({ ...profile, weightLb: event.target.value })} />
            </Field>
            <Field label="Length">
              <input placeholder="in" value={profile.lengthIn} onChange={(event) => setProfile({ ...profile, lengthIn: event.target.value })} />
            </Field>
            <Field label="Rocket diameter">
              <input type="number" min="0" step="any" placeholder="in" value={profile.rocketDiameterIn} onChange={(event) => setProfile({ ...profile, rocketDiameterIn: event.target.value })} />
            </Field>
            <Field label="Is this a cert flight?">
              <select value={certFlight} onChange={(event) => setCertFlight(event.target.value)}>
                <option>No</option>
                <option>Yes</option>
              </select>
            </Field>
            {certFlight === 'Yes' ? (
              <Field label="Cert attempt">
                <select value={certAttempt} onChange={(event) => setCertAttempt(event.target.value)}>
                  <option value=""></option>
                  <option>TMP</option>
                  <option>JrHPP</option>
                  <option>L1</option>
                  <option>L2</option>
                  <option>L3</option>
                </select>
              </Field>
            ) : null}
          </div>
        </Section>
        <Section title="Memberships">
          <div className="checkboxLine">
            <span>Membership organization</span>
            {['Tripoli', 'NAR'].map((org) => (
              <label key={org}>
                <input type="checkbox" checked={membershipOrgs.includes(org)} onChange={() => toggleMembership(org)} />
                {org}
              </label>
            ))}
          </div>
          <div className="grid four">
            {membershipOrgs.includes('Tripoli') ? (
              <>
                <Field label="Tripoli membership number" required>
                  <input value={profile.tripoliNumber} onChange={(event) => setProfile({ ...profile, tripoliNumber: event.target.value })} required />
                </Field>
                <Field label="Tripoli expiration" required>
                  <input type="date" value={profile.tripoliExpiration} onChange={(event) => setProfile({ ...profile, tripoliExpiration: event.target.value })} required />
                </Field>
              </>
            ) : null}
            {membershipOrgs.includes('NAR') ? (
              <>
                <Field label="NAR membership number" required>
                  <input value={profile.narNumber} onChange={(event) => setProfile({ ...profile, narNumber: event.target.value })} required />
                </Field>
                <Field label="NAR expiration" required>
                  <input type="date" value={profile.narExpiration} onChange={(event) => setProfile({ ...profile, narExpiration: event.target.value })} required />
                </Field>
              </>
            ) : null}
            <Field label="URRG member?" required>
              <select value={profile.urrgMember} onChange={(event) => setProfile({ ...profile, urrgMember: event.target.value })} required>
                <option value=""></option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </Field>
          </div>
        </Section>
        <Section title="Flight Details">
          <FlightDetailsFields
            altitude={altitude}
            setAltitude={setAltitude}
            launchGuide={launchGuide}
            setLaunchGuide={setLaunchGuide}
            flightType={flightType}
            setFlightType={setFlightType}
            motors={motors}
            setMotors={setMotors}
            towerNeeded={towerNeeded}
            setTowerNeeded={setTowerNeeded}
            towerSource={towerSource}
            setTowerSource={setTowerSource}
            recoveryMethod={recoveryMethod}
            setRecoveryMethod={setRecoveryMethod}
            recoveryOther={recoveryOther}
            setRecoveryOther={setRecoveryOther}
            redundantDeployment={redundantDeployment}
            setRedundantDeployment={setRedundantDeployment}
            redundancyMethod={redundancyMethod}
            setRedundancyMethod={setRedundancyMethod}
            flightComputer={flightComputer}
            setFlightComputer={setFlightComputer}
            mainDeployAltitude={mainDeployAltitude}
            setMainDeployAltitude={setMainDeployAltitude}
            tracking={tracking}
            setTracking={setTracking}
            trackingType={trackingType}
            setTrackingType={setTrackingType}
            specialInfo={specialInfo}
            setSpecialInfo={setSpecialInfo}
            notes={notes}
            setNotes={setNotes}
          />
        </Section>
        <ActionBar result={result} isSubmitting={isSubmitting} />
      </form>
    </PageShell>
  );
}

function CertificationFlightPage() {
  const { result, isSubmitting, submit: submitForm } = useSubmission();
  const launchSelection = useLaunchEventSelection();
  const [motors, setMotors] = useState<MotorRow[]>([blankMotor()]);
  const [altitude, setAltitude] = useState('10000');
  const [launchGuide, setLaunchGuide] = useState('');
  const [flightType, setFlightType] = useState('Standard');
  const [towerNeeded, setTowerNeeded] = useState('No');
  const [towerSource, setTowerSource] = useState('Own tower');
  const [recoveryMethod, setRecoveryMethod] = useState('');
  const [recoveryOther, setRecoveryOther] = useState('');
  const [redundantDeployment, setRedundantDeployment] = useState('No');
  const [redundancyMethod, setRedundancyMethod] = useState('');
  const [flightComputer, setFlightComputer] = useState('');
  const [mainDeployAltitude, setMainDeployAltitude] = useState('');
  const [tracking, setTracking] = useState('No');
  const [trackingType, setTrackingType] = useState('');
  const [specialInfo, setSpecialInfo] = useState('');
  const [notes, setNotes] = useState('');
  const [certOrg, setCertOrg] = useState('');
  const [certAttempt, setCertAttempt] = useState('');
  const [certStatements, setCertStatements] = useState<string[]>([]);
  const [form, setForm] = useState({
    flyerName: '',
    flyerEmail: '',
    flyerPhone: '',
    birthDate: '',
    currentCertLevel: 'None',
    schoolName: '',
    hometown: '',
    traNumber: '',
    narNumber: '',
    traExpDate: '',
    narExpDate: '',
    streetAddress: '',
    city: '',
    state: '',
    zipCode: '',
    urrgMember: '',
    cg: '',
    cp: '',
    diameter: '',
    rocketName: '',
    rocketColor: '',
    weightLb: '',
    lengthIn: '',
    kitManufacturer: '',
    totalImpulse: '',
    electronics: '',
    mentorInfo: '',
    otherComments: '',
  });

  const orgWarning = !certOrg
    ? 'Select the official certification form to prepare.'
    : certOrg === 'TRA' && (!form.traNumber || !form.traExpDate)
      ? 'TRA number and expiration date are required for a Tripoli certification.'
      : certOrg === 'NAR' && (!form.narNumber || !form.narExpDate)
        ? 'NAR number and expiration date are required for an NAR certification.'
        : '';

  function toggleStatement(value: string) {
    setCertStatements(certStatements.includes(value)
      ? certStatements.filter((statement) => statement !== value)
      : [...certStatements, value]);
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    await submitForm('certification-flight', {
      ...form,
      certOrg,
      certAttempt,
      certStatements,
      altitude,
      launchGuide,
      flightType: 'Standard',
      towerNeeded,
      towerSource: towerNeeded === 'Yes' ? towerSource : '',
      recoveryMethod,
      recoveryOther: recoveryMethod === 'Other' ? recoveryOther : '',
      redundantDeployment,
      redundancyMethod: redundantDeployment === 'Yes' ? redundancyMethod : '',
      flightComputer: recoveryMethod === 'Dual deployment' ? flightComputer : '',
      mainDeployAltitude: recoveryMethod === 'Dual deployment' ? mainDeployAltitude : '',
      tracking,
      trackingType: tracking === 'Yes' ? trackingType : '',
      specialInfo,
      notes,
      motors: motors.slice(0, 1).map((motor) => ({ ...motor, count: '1' })),
    }, getHoneypot(formElement), launchSelection.selectedEventId);
  }

  return (
    <PageShell title="Certification Flight" eyebrow="Cert form">
      <form onSubmit={submit}>
        <Honeypot />
        <LaunchWeekendSelector selection={launchSelection} />
        <Section title="Flyer">
          <div className="grid four">
            <Field label="Flyer name" required>
              <input value={form.flyerName} onChange={(event) => setForm({ ...form, flyerName: event.target.value })} required />
            </Field>
            <Field label="Flyer email" required help="Used by URRG to follow up about this certification flight.">
              <input type="email" value={form.flyerEmail} onChange={(event) => setForm({ ...form, flyerEmail: event.target.value })} required />
            </Field>
            <Field label="Phone">
              <input value={form.flyerPhone} onChange={(event) => setForm({ ...form, flyerPhone: event.target.value })} />
            </Field>
            <Field label="Birth date" required>
              <input type="date" value={form.birthDate} onChange={(event) => setForm({ ...form, birthDate: event.target.value })} required />
            </Field>
            <Field label="Cert attempt" required>
              <select value={certAttempt} onChange={(event) => setCertAttempt(event.target.value)} required>
                <option value=""></option>
                <option>L1</option>
                <option>L2</option>
                <option>L3</option>
              </select>
            </Field>
            <Field label="Current cert level">
              <select value={form.currentCertLevel} onChange={(event) => setForm({ ...form, currentCertLevel: event.target.value })}>
                <option>None</option>
                <option>TMP/JrHPP</option>
                <option>L1</option>
                <option>L2</option>
                <option>L3</option>
              </select>
            </Field>
            <Field label="URRG member" required>
              <select value={form.urrgMember} onChange={(event) => setForm({ ...form, urrgMember: event.target.value })} required>
                <option value=""></option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </Field>
            <Field label="Hometown">
              <input value={form.hometown} onChange={(event) => setForm({ ...form, hometown: event.target.value })} />
            </Field>
            <Field label="Organization or team">
              <input value={form.schoolName} onChange={(event) => setForm({ ...form, schoolName: event.target.value })} />
            </Field>
          </div>
          <AgeNotice birthDate={form.birthDate} certAttempt={certAttempt} />
        </Section>
        <Section title="Certification Organization">
          <div className="checkboxLine">
            <span>Official form</span>
            <label>
              <input type="radio" name="certOrg" checked={certOrg === 'TRA'} onChange={() => setCertOrg('TRA')} required />
              Tripoli (TRA)
            </label>
            <label>
              <input type="radio" name="certOrg" checked={certOrg === 'NAR'} onChange={() => setCertOrg('NAR')} />
              NAR
            </label>
          </div>
          {orgWarning ? <p className="callout warn">{orgWarning}</p> : null}
          <div className="grid four">
            <Field label="TRA number">
              <input value={form.traNumber} onChange={(event) => setForm({ ...form, traNumber: event.target.value })} required={certOrg === 'TRA'} />
            </Field>
            <Field label="TRA exp date">
              <input type="date" value={form.traExpDate} onChange={(event) => setForm({ ...form, traExpDate: event.target.value })} required={certOrg === 'TRA'} />
            </Field>
            <Field label="NAR number">
              <input value={form.narNumber} onChange={(event) => setForm({ ...form, narNumber: event.target.value })} required={certOrg === 'NAR'} />
            </Field>
            <Field label="NAR exp date">
              <input type="date" value={form.narExpDate} onChange={(event) => setForm({ ...form, narExpDate: event.target.value })} required={certOrg === 'NAR'} />
            </Field>
          </div>
        </Section>
        {certOrg === 'NAR' ? (
          <Section title="NAR Applicant Address">
            <div className="grid four">
              <Field label="Street address" required>
                <input value={form.streetAddress} onChange={(event) => setForm({ ...form, streetAddress: event.target.value })} required />
              </Field>
              <Field label="City" required>
                <input value={form.city} onChange={(event) => setForm({ ...form, city: event.target.value })} required />
              </Field>
              <Field label="State" required>
                <input value={form.state} onChange={(event) => setForm({ ...form, state: event.target.value })} required />
              </Field>
              <Field label="ZIP code" required>
                <input value={form.zipCode} onChange={(event) => setForm({ ...form, zipCode: event.target.value })} required />
              </Field>
            </div>
          </Section>
        ) : null}
        <Section title="Rocket Stability">
          <div className="grid four">
            <Field label="Rocket name" required>
              <input value={form.rocketName} onChange={(event) => setForm({ ...form, rocketName: event.target.value })} required />
            </Field>
            <Field label="Rocket color">
              <input value={form.rocketColor} onChange={(event) => setForm({ ...form, rocketColor: event.target.value })} />
            </Field>
            <Field label="Weight" required>
              <input type="number" min="0" step="any" placeholder="lb" value={form.weightLb} onChange={(event) => setForm({ ...form, weightLb: event.target.value })} required />
            </Field>
            <Field label="Length">
              <input placeholder="in" value={form.lengthIn} onChange={(event) => setForm({ ...form, lengthIn: event.target.value })} />
            </Field>
            <Field label="CG from nose">
              <input type="number" min="0" step="any" placeholder="in" value={form.cg} onChange={(event) => setForm({ ...form, cg: event.target.value })} required />
            </Field>
            <Field label="CP from nose">
              <input type="number" min="0" step="any" placeholder="in" value={form.cp} onChange={(event) => setForm({ ...form, cp: event.target.value })} required />
            </Field>
            <Field label="Body diameter">
              <input type="number" min="0" step="any" placeholder="in" value={form.diameter} onChange={(event) => setForm({ ...form, diameter: event.target.value })} required />
            </Field>
            <Field label="Kit name / manufacturer">
              <input value={form.kitManufacturer} onChange={(event) => setForm({ ...form, kitManufacturer: event.target.value })} />
            </Field>
            <Field label="Total impulse" required>
              <input placeholder="Class or Ns" value={form.totalImpulse} onChange={(event) => setForm({ ...form, totalImpulse: event.target.value })} required />
            </Field>
          </div>
          <StabilityNotice cg={form.cg} cp={form.cp} diameter={form.diameter} />
        </Section>
        <Section title="Certification Statements">
          <div className="checkboxLine">
            <label>
              <input type="checkbox" checked={certStatements.includes('built')} onChange={() => toggleStatement('built')} />
              I built this rocket myself.
            </label>
            <label>
              <input type="checkbox" checked={certStatements.includes('prepared')} onChange={() => toggleStatement('prepared')} />
              I prepared this rocket for flight myself.
            </label>
            <label>
              <input type="checkbox" checked={certStatements.includes('motor')} onChange={() => toggleStatement('motor')} />
              I assembled the motor myself.
            </label>
            <label>
              <input type="checkbox" checked={certStatements.includes('l3')} onChange={() => toggleStatement('l3')} />
              For L3, I have successful electronic-deployment experience.
            </label>
          </div>
          <div className="grid four">
            <Field label="Electronics">
              <input value={form.electronics} onChange={(event) => setForm({ ...form, electronics: event.target.value })} />
            </Field>
            <Field label="Mentor information">
              <input value={form.mentorInfo} onChange={(event) => setForm({ ...form, mentorInfo: event.target.value })} />
            </Field>
            <Field label="Other comments">
              <input value={form.otherComments} onChange={(event) => setForm({ ...form, otherComments: event.target.value })} />
            </Field>
          </div>
        </Section>
        <Section title="Flight Details">
          <FlightDetailsFields
            altitude={altitude}
            setAltitude={setAltitude}
            launchGuide={launchGuide}
            setLaunchGuide={setLaunchGuide}
            flightType={flightType}
            setFlightType={setFlightType}
            motors={motors}
            setMotors={setMotors}
            towerNeeded={towerNeeded}
            setTowerNeeded={setTowerNeeded}
            towerSource={towerSource}
            setTowerSource={setTowerSource}
            recoveryMethod={recoveryMethod}
            setRecoveryMethod={setRecoveryMethod}
            recoveryOther={recoveryOther}
            setRecoveryOther={setRecoveryOther}
            redundantDeployment={redundantDeployment}
            setRedundantDeployment={setRedundantDeployment}
            redundancyMethod={redundancyMethod}
            setRedundancyMethod={setRedundancyMethod}
            flightComputer={flightComputer}
            setFlightComputer={setFlightComputer}
            mainDeployAltitude={mainDeployAltitude}
            setMainDeployAltitude={setMainDeployAltitude}
            tracking={tracking}
            setTracking={setTracking}
            trackingType={trackingType}
            setTrackingType={setTrackingType}
            specialInfo={specialInfo}
            setSpecialInfo={setSpecialInfo}
            notes={notes}
            setNotes={setNotes}
            standardOnly
          />
        </Section>
        <ActionBar result={result} isSubmitting={isSubmitting} />
      </form>
    </PageShell>
  );
}

function ProjectFlightPage() {
  const { result, isSubmitting, submit: submitForm } = useSubmission();
  const launchSelection = useLaunchEventSelection();
  const [motors, setMotors] = useState<MotorRow[]>([blankMotor()]);
  const [altitude, setAltitude] = useState('10000');
  const [launchGuide, setLaunchGuide] = useState('');
  const [flightType, setFlightType] = useState('Standard');
  const [towerNeeded, setTowerNeeded] = useState('No');
  const [towerSource, setTowerSource] = useState('Own tower');
  const [recoveryMethod, setRecoveryMethod] = useState('');
  const [recoveryOther, setRecoveryOther] = useState('');
  const [redundantDeployment, setRedundantDeployment] = useState('No');
  const [redundancyMethod, setRedundancyMethod] = useState('');
  const [flightComputer, setFlightComputer] = useState('');
  const [mainDeployAltitude, setMainDeployAltitude] = useState('');
  const [tracking, setTracking] = useState('No');
  const [trackingType, setTrackingType] = useState('');
  const [specialInfo, setSpecialInfo] = useState('');
  const [notes, setNotes] = useState('');
  const [membershipOrgs, setMembershipOrgs] = useState<string[]>([]);
  const [form, setForm] = useState({
    ownerType: 'Team',
    ownerName: '',
    flyerName: '',
    flyerEmail: '',
    flyerPhone: '',
    hometown: '',
    schoolName: '',
    currentCertLevel: '',
    rocketName: '',
    rocketColor: '',
    weightLb: '',
    lengthIn: '',
    rocketDiameterIn: '',
    tripoliNumber: '',
    tripoliExpiration: '',
    narNumber: '',
    narExpiration: '',
    urrgMember: '',
    projectSummary: '',
    specialNeeds: '',
  });

  function toggleMembership(org: string) {
    setMembershipOrgs(
      membershipOrgs.includes(org)
        ? membershipOrgs.filter((item) => item !== org)
        : [...membershipOrgs, org],
    );
  }

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    await submitForm('project-flight', {
      ...form,
      membershipOrgs,
      altitude,
      launchGuide,
      flightType,
      towerNeeded,
      towerSource: towerNeeded === 'Yes' ? towerSource : '',
      recoveryMethod,
      recoveryOther: recoveryMethod === 'Other' ? recoveryOther : '',
      redundantDeployment,
      redundancyMethod: redundantDeployment === 'Yes' ? redundancyMethod : '',
      flightComputer: recoveryMethod === 'Dual deployment' ? flightComputer : '',
      mainDeployAltitude: recoveryMethod === 'Dual deployment' ? mainDeployAltitude : '',
      tracking,
      trackingType: tracking === 'Yes' ? trackingType : '',
      specialInfo,
      notes,
      motors,
    }, getHoneypot(formElement), launchSelection.selectedEventId);
  }

  return (
    <PageShell title="Project Flight" eyebrow="Project form">
      <form onSubmit={submit}>
        <Honeypot />
        <LaunchWeekendSelector selection={launchSelection} />
        <Section title="Owner and Flyer">
          <div className="grid four">
            <Field label="Owner type">
              <select value={form.ownerType} onChange={(event) => setForm({ ...form, ownerType: event.target.value })}>
                <option>Individual</option>
                <option>Team</option>
              </select>
            </Field>
            <Field label="Owner or team name" required>
              <input value={form.ownerName} onChange={(event) => setForm({ ...form, ownerName: event.target.value })} required />
            </Field>
            <Field label="Flyer name" required>
              <input value={form.flyerName} onChange={(event) => setForm({ ...form, flyerName: event.target.value })} required />
            </Field>
            <Field label="Flyer email" required help="Used by URRG to follow up about this project flight.">
              <input type="email" value={form.flyerEmail} onChange={(event) => setForm({ ...form, flyerEmail: event.target.value })} required />
            </Field>
            <Field label="Phone">
              <input value={form.flyerPhone} onChange={(event) => setForm({ ...form, flyerPhone: event.target.value })} />
            </Field>
            <Field label="Hometown">
              <input value={form.hometown} onChange={(event) => setForm({ ...form, hometown: event.target.value })} />
            </Field>
            <Field label="Organization or team">
              <input value={form.schoolName} onChange={(event) => setForm({ ...form, schoolName: event.target.value })} />
            </Field>
            <Field label="Current cert level">
              <select value={form.currentCertLevel} onChange={(event) => setForm({ ...form, currentCertLevel: event.target.value })}>
                <option value=""></option>
                <option>None</option>
                <option>TMP/JrHPP</option>
                <option>L1</option>
                <option>L2</option>
                <option>L3</option>
              </select>
            </Field>
            <Field label="Rocket name" required>
              <input value={form.rocketName} onChange={(event) => setForm({ ...form, rocketName: event.target.value })} required />
            </Field>
            <Field label="Rocket color">
              <input value={form.rocketColor} onChange={(event) => setForm({ ...form, rocketColor: event.target.value })} />
            </Field>
            <Field label="Weight">
              <input placeholder="lb" value={form.weightLb} onChange={(event) => setForm({ ...form, weightLb: event.target.value })} />
            </Field>
            <Field label="Length">
              <input placeholder="in" value={form.lengthIn} onChange={(event) => setForm({ ...form, lengthIn: event.target.value })} />
            </Field>
            <Field label="Rocket diameter">
              <input type="number" min="0" step="any" placeholder="in" value={form.rocketDiameterIn} onChange={(event) => setForm({ ...form, rocketDiameterIn: event.target.value })} />
            </Field>
          </div>
          <div className="grid two">
            <Field label="Project summary">
              <textarea value={form.projectSummary} onChange={(event) => setForm({ ...form, projectSummary: event.target.value })} />
            </Field>
            <Field label="Special needs / requests">
              <textarea value={form.specialNeeds} onChange={(event) => setForm({ ...form, specialNeeds: event.target.value })} />
            </Field>
          </div>
        </Section>
        <Section title="Memberships">
          <div className="checkboxLine">
            <span>Membership organization</span>
            {['Tripoli', 'NAR'].map((org) => (
              <label key={org}>
                <input type="checkbox" checked={membershipOrgs.includes(org)} onChange={() => toggleMembership(org)} />
                {org}
              </label>
            ))}
          </div>
          <div className="grid four">
            {membershipOrgs.includes('Tripoli') ? (
              <>
                <Field label="Tripoli membership number" required>
                  <input value={form.tripoliNumber} onChange={(event) => setForm({ ...form, tripoliNumber: event.target.value })} required />
                </Field>
                <Field label="Tripoli expiration" required>
                  <input type="date" value={form.tripoliExpiration} onChange={(event) => setForm({ ...form, tripoliExpiration: event.target.value })} required />
                </Field>
              </>
            ) : null}
            {membershipOrgs.includes('NAR') ? (
              <>
                <Field label="NAR membership number" required>
                  <input value={form.narNumber} onChange={(event) => setForm({ ...form, narNumber: event.target.value })} required />
                </Field>
                <Field label="NAR expiration" required>
                  <input type="date" value={form.narExpiration} onChange={(event) => setForm({ ...form, narExpiration: event.target.value })} required />
                </Field>
              </>
            ) : null}
            <Field label="URRG member?" required>
              <select value={form.urrgMember} onChange={(event) => setForm({ ...form, urrgMember: event.target.value })} required>
                <option value=""></option>
                <option>Yes</option>
                <option>No</option>
              </select>
            </Field>
          </div>
        </Section>
        <Section title="Flight Details">
          <FlightDetailsFields
            altitude={altitude}
            setAltitude={setAltitude}
            launchGuide={launchGuide}
            setLaunchGuide={setLaunchGuide}
            flightType={flightType}
            setFlightType={setFlightType}
            motors={motors}
            setMotors={setMotors}
            towerNeeded={towerNeeded}
            setTowerNeeded={setTowerNeeded}
            towerSource={towerSource}
            setTowerSource={setTowerSource}
            recoveryMethod={recoveryMethod}
            setRecoveryMethod={setRecoveryMethod}
            recoveryOther={recoveryOther}
            setRecoveryOther={setRecoveryOther}
            redundantDeployment={redundantDeployment}
            setRedundantDeployment={setRedundantDeployment}
            redundancyMethod={redundancyMethod}
            setRedundancyMethod={setRedundancyMethod}
            flightComputer={flightComputer}
            setFlightComputer={setFlightComputer}
            mainDeployAltitude={mainDeployAltitude}
            setMainDeployAltitude={setMainDeployAltitude}
            tracking={tracking}
            setTracking={setTracking}
            trackingType={trackingType}
            setTrackingType={setTrackingType}
            specialInfo={specialInfo}
            setSpecialInfo={setSpecialInfo}
            notes={notes}
            setNotes={setNotes}
          />
        </Section>
        <ActionBar result={result} isSubmitting={isSubmitting} />
      </form>
    </PageShell>
  );
}

export function App() {
  const [route, setRoute] = useState<Route>(getRouteFromPath);

  useEffect(() => {
    const sync = () => setRoute(getRouteFromPath());
    window.addEventListener('popstate', sync);
    return () => window.removeEventListener('popstate', sync);
  }, []);

  useEffect(() => {
    const metadata = routePageMetadata[route];
    document.title = metadata.title;
    const icon = document.querySelector<HTMLLinkElement>('#route-favicon');
    if (icon) icon.href = `${basePath}/icons/${metadata.icon}.ico`;
  }, [route]);

  const page = useMemo(() => {
    switch (route) {
      case 'manifest':
        return <ManifestPage />;
      case 'flight-card':
        return <FlightCardPage />;
      case 'certification-flight':
        return <CertificationFlightPage />;
      case 'project-flight':
        return <ProjectFlightPage />;
      case 'team-directory':
        return <TeamDirectoryPage />;
      case 'team-summary':
        return <TeamSummaryPage />;
      case 'launch-summary':
        return <LaunchDaySummaryPage />;
      case 'launch-operations':
        return <LaunchOperationsPage />;
      case 'admins':
        return <AdminManagementPage />;
      default:
        return <HomePage />;
    }
  }, [route]);

  return <><UrrgHeader />{page}</>;
}
